import { arrayMove } from "@dnd-kit/sortable";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import ExportDeliveryNotice from "../../components/Reports/ExportDeliveryNotice";
import SourcesUsed from "../../components/Reports/SourcesUsed";
import { artifactUsable } from "../../utils/artifactQuality";

import SharedAutoGrowTextarea from "../../components/AutoGrowTextarea/AutoGrowTextarea";
import CompsEditor, {
  compAddresses,
  unverifiedCompAddresses,
} from "../../components/CompsEditor/CompsEditor";
import AddToListing, { useCreditsAccount } from "../../components/Credits/AddToListing";
import BalanceChip from "../../components/Credits/BalanceChip";
import ConfirmSpendSheet from "../../components/Credits/ConfirmSpendSheet";
import CreditsPaywallSheet from "../../components/Credits/CreditsPaywallSheet";
import NewFooter from "../../components/Footers/NewFooter";
import InputDocumentUploader from "../../components/InputDocumentUploader/InputDocumentUploader";
import NewNavbar from "../../components/Navbar/NewNavbar";
import ReportPreview from "../../components/ReportPreview/ReportPreview";
import SortablePhotoGrid from "../../components/SortablePhotoGrid/SortablePhotoGrid";
import { API_URL } from "../../constants";
import { formatCredits, insufficientCreditsFrom } from "../../constants/credits";
import usePaidAttempt from "../../hooks/usePaidAttempt";
import usePollReportDocument from "../../hooks/usePollReportDocument";
import { track } from "../../utils/analytics";
import { authHeader, clearToken, getToken } from "../../utils/auth";
import { enumValue, humanizeKey } from "../../utils/format";
import { fileToImageDataUrl } from "../../utils/image";
import { cleanPhotoName, photoUrlFromManifest } from "../../utils/photos";
import { clearReportDraft, loadReportDraft, saveReportDraft } from "../../utils/reportDrafts";
import {
  jobFailureMessage,
  reportJobId,
  reportVersion,
  savedWriteResponse,
} from "../../utils/reportJobs";
import { changedSectionIds } from "../../utils/reportSections";
import { storageGet, storageSet } from "../../utils/storage";

/**
 * In-app report editor. The report is an editable FIRST DRAFT: agents tweak the
 * prose, drop or reorder sections, then save — which re-renders the PDF/HTML
 * from the edited structured content with no AI involved. Addressed by the
 * computation uuid as a capability (same anonymous model as /touchups/:uuid);
 * ownership-based auth lands with accounts.
 *
 * Only prose fields are editable here. Comp tables and the suggested price range
 * are intentionally locked so the numbers can't be made to contradict each other
 * (those change via "regenerate", not hand-editing).
 *
 * @typedef {Record<string, any>} Section
 */

/**
 * Editable prose + list fields per section type. Anything not listed (photo
 * sequence / photo analysis) is shown read-only but can still be reordered or
 * removed.
 * @type {Record<string, { label: string, text: string[], lists: string[], locked?: string }>}
 */
const SECTION_FIELDS = {
  ReportOverviewSection: {
    label: "Overview",
    text: ["brief_summary"],
    lists: ["key_highlights"],
  },
  ExecutiveSummarySection: {
    label: "Executive Summary",
    text: ["summary", "primary_opportunity", "target_buyer"],
    lists: ["key_assets"],
  },
  NeighborhoodOverviewSection: {
    label: "Neighborhood",
    text: [
      "overview_text",
      "property_positioning",
      "amenities_lifestyle",
      "schools_education",
      "market_dynamics",
      "commute_accessibility",
    ],
    lists: [],
  },
  ListingRewriteSection: {
    label: "Listing Description",
    text: ["rewritten_description", "target_buyer_appeal"],
    lists: ["neighborhood_highlights"],
  },
  ComparableAnalysisSection: {
    label: "Comparables & Pricing",
    text: ["market_positioning", "current_market_conditions", "pricing_strategy"],
    lists: [],
    locked:
      "Edit any comparable's details above — $/SF and the vs-subject numbers recompute as you type. The suggested range and verdict are rebuilt when you update the comparables with AI.",
  },
  TaxRecordsSection: {
    label: "Property Tax History",
    // The trend note is editable prose; the tax table itself is public-records
    // data and stays locked (like the comp tables).
    text: ["trend_note"],
    lists: [],
    locked:
      "Tax figures come from public records and stay locked. You can edit the summary note; use Regenerate to refresh the data.",
  },
  PriceHistorySection: {
    label: "Price & Sale History",
    text: ["trend_note"],
    lists: [],
    locked:
      "Price history comes from public records and stays locked. You can edit the summary note; use Regenerate to refresh the data.",
  },
  ConclusionSection: {
    label: "Conclusion & Next Steps",
    text: ["conclusion_text", "market_positioning_strategy", "final_recommendations"],
    lists: ["likely_outcomes", "watch_outs"],
  },
};

// Section types that support AI revision (mirrors the worker's REVISE_SPECS).
// The comparables/pricing narrative is hand-editable but deliberately NOT
// AI-revisable, so the model can't drift the pricing story.
const REVISABLE_TYPES = new Set([
  "ReportOverviewSection",
  "ExecutiveSummarySection",
  "NeighborhoodOverviewSection",
  "ListingRewriteSection",
  "ConclusionSection",
]);

// Auto-save fires after this idle gap. It persists edits WITHOUT re-rendering the
// PDF/exports (render deferred to Share), so it can be frequent + cheap.
// Jest still exercises the same debounce path, but does not spend 1.5 seconds
// of wall time on every persisted edit. Production behavior is unchanged.
const AUTOSAVE_DEBOUNCE_MS = process.env.NODE_ENV === "test" ? 100 : 1500;

/** localStorage key for per-section approvals (the Personalize "Looks good" ticks). */
/** @param {string} uuid */
const approvalsKey = (uuid) => `liq_approvals:${uuid}`;
/** @param {string} uuid @returns {Record<string, boolean>} */
const loadApprovals = (uuid) => {
  try {
    return JSON.parse(storageGet(approvalsKey(uuid), "local") || "{}") || {};
  } catch {
    return {};
  }
};
/** @param {string} uuid @param {Record<string, boolean>} value */
const saveApprovals = (uuid, value) => {
  storageSet(approvalsKey(uuid), JSON.stringify(value), "local");
};

// Report documents are JSON values. Keep an immutable load-time draft so a
// section removal or a series of edits is always reversible without another
// network request.
/** @template T @param {T} value @returns {T} */
const cloneJson = (value) => JSON.parse(JSON.stringify(value ?? null));

// The two-pane layout kicks in at 1100px (matches EditorLayout's media query).
// jsdom has no matchMedia — treated as mobile, where scroll calls no-op anyway.
const isDesktopViewport = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(min-width: 1100px)").matches;

/** @param {string} key */

/** @param {Section} section */
const sectionTitle = (section) =>
  section.title ||
  SECTION_FIELDS[section.__type__]?.label ||
  humanizeKey(section.__type__ || "Section");

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 140px;
`;

const Eyebrow = styled.p`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin: 0 0 8px 0;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #4a4a4a;
  margin: 0 0 18px 0;
`;

// -------------------------------------------------------------------------
// Two-pane WYSIWYG layout: editing rail on the left, a live document preview
// on the right (desktop). On mobile the panes become Edit / Preview tabs.
// -------------------------------------------------------------------------
const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  align-items: start;

  @media (min-width: 1100px) {
    grid-template-columns: minmax(0, 10fr) minmax(0, 11fr);
    gap: 32px;
  }
`;

const EditRail = styled.div`
  @media (max-width: 1099.98px) {
    display: ${(p) => (p.$mobileHidden ? "none" : "block")};
  }
`;

const PreviewRail = styled.div`
  @media (max-width: 1099.98px) {
    display: ${(p) => (p.$mobileHidden ? "none" : "block")};
  }

  @media (min-width: 1100px) {
    position: sticky;
    top: 88px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
    /* room so the sheet's shadow isn't clipped by the scroll container */
    padding: 2px 6px 24px 2px;
  }
`;

const PreviewBarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const PreviewBarTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
`;

const ExportButton = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #0b1c1e;
  background: #0b1c1e;
  color: #ffffff;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #12302f;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ViewTabs = styled.div`
  display: flex;
  gap: 4px;
  margin: 0 0 16px;
  padding: 4px;
  background: #f3f4f6;
  border-radius: 999px;

  @media (min-width: 1100px) {
    display: none;
  }
`;

const ViewTab = styled.button`
  flex: 1;
  min-height: 42px;
  border-radius: 999px;
  border: none;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#ffffff" : "transparent")};
  color: ${(p) => (p.$active ? "#0b1c1e" : "#4b5563")};
  box-shadow: ${(p) => (p.$active ? "0 1px 4px rgba(11, 28, 30, 0.12)" : "none")};
`;

// "It's done" moment for a fresh, never-edited draft: the package exists and
// the preview IS the document.
const ReadyBanner = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  background: #f0faf9;
  border: 1px solid #bfe0dc;
  border-radius: 12px;
  padding: 14px 18px;
  margin: 0 0 20px;
  color: #0b605d;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ReadyTick = styled.span`
  font-weight: 800;
  font-size: 1.05rem;
`;

const ReadOnlyBanner = styled.div`
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 14px 18px;
  margin: 0 0 24px 0;
  color: #78350f;
  font-size: 0.92rem;

  a {
    color: #78350f;
    font-weight: 700;
    text-decoration: underline;
  }
`;

const RegenerateRow = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 18px;
  margin: 0 0 24px 0;
  font-size: 0.9rem;
  color: #4a4a4a;
`;

const RegenerateTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 6px;
`;

const RegenerateHint = styled.p`
  margin: 0 0 12px;
  line-height: 1.5;
`;

// Pricing v2: the re-prepare quote as one quiet fact where the choice is made.
const RegenerateCost = styled.p`
  margin: -6px 0 12px;
  font-size: 0.86rem;
  font-weight: 600;
  color: #374151;
`;

const RegenerateControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;

  @media (max-width: 540px) {
    flex-direction: column;
  }
`;

const RegenerateGuidanceInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: 0.92rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
  }
  &:disabled {
    background: #f3f4f6;
  }
`;

const RegenerateButton = styled.button`
  font-size: 0.88rem;
  font-weight: 600;
  color: #1a1a1a;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  padding: 9px 20px;
  min-height: 44px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SectionCard = styled.section`
  /* Scroll-sync landings use scrollIntoView(start): leave room for the fixed
     navbar plus a breath of air so the section title lands visibly below it. */
  scroll-margin-top: 96px;
  border: 1px solid ${(p) => (p.$approved ? "#bfe0dc" : "#e5e7eb")};
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 14px;
  background: ${(p) => (p.$approved ? "#f6fbfa" : "#ffffff")};
  transition: background 0.15s ease, border-color 0.15s ease;
`;

const BrandingCard = styled.section`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px 22px;
  margin-bottom: 24px;
  background: #fafafa;
`;

const BrandingHeader = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0 0 14px 0;
`;

const BrandingGrid = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  /* Keep every label on the same top edge. Feedback mixes a short select with
     a taller textarea, so bottom alignment made the select label appear to
     float halfway down the row. */
  align-items: flex-start;

  @media (max-width: 540px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const BrandingField = styled.div`
  flex: 1;
  min-width: 160px;
  display: flex;
  flex-direction: column;
`;

const BrandingColorField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorSwatch = styled.input`
  width: 42px;
  height: 38px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: none;
  cursor: pointer;
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 0;
  flex-wrap: wrap;
`;

const LogoPreview = styled.img`
  height: 48px;
  max-width: 200px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
  background: #ffffff;
`;

const LogoUploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
  margin: 0;

  &:hover {
    background: #f3f4f6;
  }
  /* The real <input> is visually hidden but focusable; mirror its focus ring
     onto this label so keyboard users get a visible target. */
  &:focus-within {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }
  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Visually hidden but kept in the tab order + screen-reader tree (unlike
// display:none), so the logo upload is reachable by keyboard.
const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const LogoError = styled.p`
  font-size: 0.82rem;
  color: #b91c1c;
  margin: 8px 0 0;
`;

const BrandingToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
`;

const SectionName = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

/** Per-section indicator of how AI relates to it: AI-revisable (teal), the
 * numbers-locked comps/pricing section (amber), or a photo-built read-only
 * section (gray). Colors are passed as props so one component covers all. */
const AiBadge = styled.span`
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$bd};
`;

const SectionControls = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 10px;
  min-height: 44px;
  min-width: 44px;
  cursor: pointer;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:focus-visible {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const RemoveButton = styled(IconButton)`
  color: #b91c1c;
  border-color: #fecaca;
  &:hover:not(:disabled) {
    background: #fef2f2;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 14px 0 6px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 84px;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 11px 13px;
  /* The field grows to fit its content (see AutoGrowTextarea) so the agent
     always sees the whole passage — never an inner scrollbar. */
  resize: none;
  overflow: hidden;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/**
 * A textarea that auto-sizes to its content so the full passage is always
 * visible without an inner scrollbar. Resizes on every value change (edits and
 * AI revisions both flow through `value`) and on first paint.
 * @param {{ value: string, onChange: (e: any) => void, [k: string]: any }} props
 */
const AutoGrowTextarea = (props) => (
  <SharedAutoGrowTextarea textareaComponent={Textarea} {...props} />
);

// Compact auto-grow variant for LIST items (key assets, highlights, watch-outs):
// single-row height when short, wraps and grows when long — the whole item is
// always readable and editable, never clipped inside a one-line input.
const ListItemField = styled(Textarea)`
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 8px;
  line-height: 1.45;
`;

// List items are single entries, not paragraphs — swallow Enter so a stray
// keypress can't inject newlines into a bullet.
/** @param {React.KeyboardEvent} e */
const blockNewlines = (e) => {
  if (e.key === "Enter") e.preventDefault();
};

const ListRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  /* Items grow with their text; keep the remove button pinned to the top. */
  align-items: flex-start;
`;

const ListInput = styled.input`
  flex: 1;
  font-size: 0.95rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #6b7280;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// The generic field styles above also serve dense, repeating section editors.
// Advanced cards need a calmer form rhythm: one label gap, full-width controls,
// and an explicit break before the feedback action.
const AdvancedFieldLabel = styled(FieldLabel)`
  margin: 0 0 6px;
`;

const AdvancedControl = styled(ListInput)`
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 44px;
  box-sizing: border-box;
  line-height: 1.4;
`;

const BrandingAssetLabel = styled(AdvancedFieldLabel)`
  margin-top: 18px;
`;

const FeedbackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const AddButton = styled.button`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 8px 14px;
  min-height: 44px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LockedNote = styled.p`
  font-size: 0.85rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 4px 0 0;
`;

const ReadOnlyNote = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
`;

// --- Photo section editor ---
const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
  margin: 4px 0 4px;
`;

const PhotoCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #fafafa;
  display: flex;
  flex-direction: column;
`;

const PhotoThumbWrap = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  background: #f3f4f6;
`;

const PhotoThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PhotoMissing = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  color: #9ca3af;
`;

const LeadTag = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #ffffff;
  background: rgba(14, 126, 122, 0.92);
  padding: 3px 8px;
  border-radius: 999px;
`;

const PhotoRemove = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(17, 24, 39, 0.72);
  color: #ffffff;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(185, 28, 28, 0.92);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PhotoCaption = styled.input`
  width: 100%;
  border: none;
  border-top: 1px solid #e5e7eb;
  font-size: 0.82rem;
  color: #374151;
  padding: 8px 10px;
  background: #ffffff;
  font-family: inherit;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    background: #f0faf9;
  }
  &::placeholder {
    color: #9ca3af;
  }
`;

// Per-card controls: reorder arrows (work everywhere, incl. touch) + Replace.
const PhotoToolRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 2px 6px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
`;

const PhotoMoveBtn = styled.button`
  min-width: 40px;
  min-height: 40px;
  border: none;
  background: none;
  color: #374151;
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: 8px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

/** @type {import("styled-components").StyledComponent<"label", any, { $disabled?: boolean }, never>} */
const PhotoReplaceLabel = styled.label`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${(/** @type {{ $disabled?: boolean }} */ p) => (p.$disabled ? "#9ca3af" : "#0e7e7a")};
  cursor: ${(/** @type {{ $disabled?: boolean }} */ p) =>
    p.$disabled ? "not-allowed" : "pointer"};
  border-radius: 8px;

  &:hover {
    background: ${(/** @type {{ $disabled?: boolean }} */ p) =>
      p.$disabled ? "transparent" : "#f0faf9"};
  }
  &:focus-within {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }
`;

/** @type {import("styled-components").StyledComponent<"label", any, { $disabled?: boolean }, never>} */
const AddPhotoLabel = styled.label`
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(/** @type {{ $disabled?: boolean }} */ p) => (p.$disabled ? "#9ca3af" : "#374151")};
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 8px 14px;
  min-height: 44px;
  margin-top: 8px;
  cursor: ${(/** @type {{ $disabled?: boolean }} */ p) =>
    p.$disabled ? "not-allowed" : "pointer"};

  &:hover {
    background: ${(/** @type {{ $disabled?: boolean }} */ p) =>
      p.$disabled ? "#f9fafb" : "#f3f4f6"};
  }
  &:focus-within {
    outline: 2px solid #0e7e7a;
    outline-offset: 2px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
  }
`;

const PhotoErrorText = styled.p`
  font-size: 0.82rem;
  color: #b91c1c;
  margin: 6px 0 0;
`;

const PhotosEmptyNote = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  font-style: italic;
  margin: 4px 0;
`;

const ReviseBox = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #e5e7eb;
`;

const ReviseLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0e7e7a;
  margin-bottom: 6px;
`;

const ReviseRow = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 540px) {
    flex-direction: column;
  }
`;

const ReviseInput = styled.input`
  flex: 1;
  font-size: 0.9rem;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 12px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
  }
  &:disabled {
    background: #f9fafb;
  }
`;

const ReviseButton = styled.button`
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: #0e7e7a;
  border: 1px solid #0e7e7a;
  border-radius: 8px;
  padding: 9px 16px;
  min-height: 44px;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #0b6360;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// One consistent "AI is working" affordance for every dynamic AI operation:
// a floating pill above the save bar with a real spinner while the job runs,
// flipping to a success tick when the refreshed content lands (the polls
// already swap the new content in — this makes the moment visible).
const AiPill = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 112px;
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: min(92vw, 560px);
  background: ${(p) => (p.$done ? "#0b605d" : "#0b1c1e")};
  color: #ffffff;
  border-radius: 999px;
  padding: 12px 20px;
  box-shadow: 0 12px 32px rgba(11, 28, 30, 0.35);

  @media (max-width: 540px) {
    bottom: 132px;
  }
`;

const AiPillSpinner = styled.span`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border: 2.5px solid rgba(255, 255, 255, 0.25);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: aipillspin 0.8s linear infinite;

  @keyframes aipillspin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-top-color: rgba(255, 255, 255, 0.25);
  }
`;

const AiPillTick = styled.span`
  font-size: 1rem;
  font-weight: 800;
  flex-shrink: 0;
`;

const AiPillText = styled.div`
  min-width: 0;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.35;
`;

const AiPillSub = styled.span`
  display: block;
  font-size: 0.78rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
`;

// Small spinner shown in the header of the section an AI job is rewriting.
const SectionSpinner = styled.span`
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 2px solid #d5ece9;
  border-top-color: #0e7e7a;
  border-radius: 50%;
  animation: aipillspin 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 14px 0;
  z-index: 50;
`;

const SaveBarInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 540px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const SaveStatusText = styled.span`
  font-size: 0.9rem;
  color: ${(/** @type {{ $kind?: string }} */ p) => (p.$kind === "error" ? "#b91c1c" : "#4a4a4a")};
`;

const SaveActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 540px) {
    justify-content: space-between;
  }
`;

const RetrySaveButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid #b91c1c;
  background: #ffffff;
  color: #b91c1c;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryLink = styled.a`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  text-decoration: underline;
`;

const CenteredMessage = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #4a4a4a;

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 12px;
  }
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ---------------------------------------------------------------------------
// Section editor
// ---------------------------------------------------------------------------
/**
 * @param {{
 *   section: Section,
 *   index: number,
 *   total: number,
 *   onField: (field: string, value: string) => void,
 *   onList: (field: string, items: string[]) => void,
 *   onRemove: () => void,
 *   onRevise: (instruction: string) => void,
 *   isRevising: boolean,
 *   disabled: boolean,
 * }} props
 */
const PHOTO_SECTION_TYPES = new Set(["PhotoAnalysisSection", "PhotoSequenceSection"]);

/** Resolve a stored photo path/filename to a browser URL via the manifest
 * (keyed by basename; names may arrive markdown-wrapped, so normalize first). */
/** @param {Record<string, string>} manifest @param {unknown} p */
const photoUrl = (manifest, p) => photoUrlFromManifest(manifest, p);

/** @param {unknown} p */
const basename = (p) => cleanPhotoName(p);

/** The section's current photos as manifest basenames, in display order. */
/** @param {Section} section @returns {string[]} */
const sectionPhotoBasenames = (section) => {
  if (section?.__type__ === "PhotoSequenceSection") {
    return (section.strategy?.complete_sequence || []).map(basename).filter(Boolean);
  }
  return (section?.assessment?.photos || []).map(basename).filter(Boolean);
};

const MakeLeadButton = styled.button`
  width: 100%;
  border: none;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  color: #0b605d;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 7px 8px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0faf9;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// A pool photo that can be added (back) into the section.
const AddCover = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(11, 28, 30, 0.35);
  color: #ffffff;
  font-size: 1.6rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(14, 126, 122, 0.75);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PhotoAiRow = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed #e5e7eb;
`;

const PhotoChangedHint = styled.p`
  font-size: 0.85rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0 0 10px;
  line-height: 1.5;
`;

const PhotoAiHint = styled.p`
  font-size: 0.85rem;
  color: #4a4a4a;
  margin: 8px 0 0;
  line-height: 1.5;
`;

/** Enum values arrive serialized as {__enum__, value}; plain strings pass through. */
/** @param {unknown} v @returns {string} */

// The recommendation levels the worker's enums accept, and the enum tag each
// field carries in the serialized document (used when a level was absent).
const REC_LEVELS = ["Low", "Medium", "High"];
const REC_LEVEL_ENUMS = {
  impact: "ImpactLevel",
  cost: "CostLevel",
  effort: "EffortLevel",
};

/**
 * Curate a photo section: reorder photos (the order IS the document's order),
 * remove the ones you don't want, add listing photos back from the pool, and
 * edit the section's visible text — the overview, captions, and the
 * recommendation cards the document actually renders. Layout-only changes
 * flow through the normal save → re-render; "Update with AI" re-runs just
 * this section's write-up around the curated photos (mirrors the comparable
 * sales flow). Handles both room galleries (PhotoAnalysisSection) and the
 * photo sequence (PhotoSequenceSection).
 * @param {{
 *   section: any,
 *   manifest: Record<string, string>,
 *   onReplace: (s: any) => void,
 *   disabled: boolean,
 *   onUpdatePhotos?: (photos: string[]) => void,
 *   isUpdating?: boolean,
 *   photosChanged?: boolean,
 *   onUploadPhoto?: (file: File) => Promise<{basename: string, url: string} | null>,
 * }} props
 */
const PhotoSectionEditor = ({
  section,
  manifest,
  onReplace,
  disabled,
  onUpdatePhotos,
  isUpdating = false,
  photosChanged = false,
  onUploadPhoto,
}) => {
  const [busyKey, setBusyKey] = useState(/** @type {number | "add" | null} */ (null));
  const [photoError, setPhotoError] = useState("");

  /**
   * @param {File | null | undefined} file
   * @param {number | "add"} key
   */
  const upload = async (file, key) => {
    if (!file || !onUploadPhoto || busyKey != null) return null;
    setPhotoError("");
    setBusyKey(key);
    try {
      const result = await onUploadPhoto(file);
      if (!result) setPhotoError("Couldn't upload that photo. Try again.");
      return result;
    } catch {
      setPhotoError("Couldn't upload that photo. Try again.");
      return null;
    } finally {
      setBusyKey(null);
    }
  };

  const controlsDisabled = disabled || isUpdating;
  const currentBasenames = sectionPhotoBasenames(section);
  const pool = Object.keys(manifest || {}).filter((name) => !currentBasenames.includes(name));

  const aiBlock = onUpdatePhotos ? (
    <PhotoAiRow>
      {photosChanged && !isUpdating && (
        <PhotoChangedHint>
          You changed the photos — the write-up below may not match them anymore.
        </PhotoChangedHint>
      )}
      <ReviseButton
        type="button"
        disabled={controlsDisabled || currentBasenames.length === 0}
        onClick={() => onUpdatePhotos(currentBasenames)}
      >
        {isUpdating ? "Updating write-up…" : "Update write-up with AI"}
      </ReviseButton>
      <PhotoAiHint>
        {isUpdating
          ? "AI is looking at your photos and rewriting this part — about a minute."
          : "AI rewrites this part's text around the photos and order shown here (about a minute). Your captions stay."}
        {!isUpdating && " Always free."}
      </PhotoAiHint>
    </PhotoAiRow>
  ) : null;

  /** @param {(name: string) => void} addPhoto */
  const renderAddPool = (addPhoto) =>
    pool.length > 0 && (
      <>
        <FieldLabel as="div">Add a photo back ({pool.length} more from your listing)</FieldLabel>
        <PhotoGrid>
          {pool.map((name) => (
            <PhotoCard key={name}>
              <PhotoThumbWrap>
                <PhotoThumb src={manifest[name]} alt="" loading="lazy" />
                <AddCover
                  type="button"
                  aria-label={`Add ${name}`}
                  disabled={controlsDisabled}
                  onClick={() => addPhoto(name)}
                >
                  +
                </AddCover>
              </PhotoThumbWrap>
            </PhotoCard>
          ))}
        </PhotoGrid>
      </>
    );

  if (section.__type__ === "PhotoSequenceSection") {
    const strategy = section.strategy || {};
    const sequence = /** @type {string[]} */ (strategy.complete_sequence || []);
    /** @param {Record<string, any>} patch */
    const setStrategy = (patch) => onReplace({ ...section, strategy: { ...strategy, ...patch } });
    // Every order mutation flows through here: position 0 is the lead, and the
    // first-five teaser tracks the top of the order. `newBasename` (replace/add)
    // must also land in available_photos so the re-render can resolve it.
    /** @param {string[]} newSeq @param {string} [newBasename] */
    const applySequence = (newSeq, newBasename) =>
      onReplace({
        ...section,
        ...(newBasename
          ? {
              available_photos: Array.from(
                new Set([...(section.available_photos || []), newBasename])
              ),
            }
          : {}),
        strategy: {
          ...strategy,
          complete_sequence: newSeq,
          first_five_sequence: newSeq.slice(0, 5),
          lead_image: newSeq[0] || "",
        },
      });
    /** @param {number} from @param {number} to */
    const movePhoto = (from, to) => {
      if (to < 0 || to >= sequence.length || from === to) return;
      const next = sequence.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      applySequence(next);
    };
    /** @param {number} i */
    const removePhoto = (i) => applySequence(sequence.filter((_, j) => j !== i));
    /** @param {number} i @param {File | null | undefined} file */
    const replacePhoto = async (i, file) => {
      const res = await upload(file, i);
      if (!res) return;
      const next = sequence.slice();
      next[i] = res.basename;
      applySequence(next, res.basename);
    };
    /** @param {File | null | undefined} file */
    const addUploadedPhoto = async (file) => {
      const res = await upload(file, "add");
      if (res) applySequence([...sequence, res.basename], res.basename);
    };
    /** @param {string} name */
    const addPoolPhoto = (name) => applySequence([...sequence, name], name);
    return (
      <>
        <FieldLabel as="div">Photo order ({sequence.length}) — drag to reorder</FieldLabel>
        {sequence.length === 0 ? (
          <PhotosEmptyNote>No photos in this sequence.</PhotosEmptyNote>
        ) : (
          <SortablePhotoGrid
            items={sequence}
            disabled={controlsDisabled}
            onReorder={(from, to) => applySequence(arrayMove(sequence, from, to))}
            container={PhotoGrid}
            itemComponent={PhotoCard}
            renderItem={(p, i) => {
              const url = photoUrl(manifest, p);
              return (
                <>
                  <PhotoThumbWrap>
                    {url ? (
                      <PhotoThumb src={url} alt="" loading="lazy" draggable={false} />
                    ) : (
                      <PhotoMissing>🖼️</PhotoMissing>
                    )}
                    {i === 0 && <LeadTag>Lead</LeadTag>}
                    <PhotoRemove
                      type="button"
                      aria-label="Remove photo"
                      disabled={controlsDisabled}
                      onClick={() => removePhoto(i)}
                    >
                      ×
                    </PhotoRemove>
                  </PhotoThumbWrap>
                  {i !== 0 && (
                    <MakeLeadButton
                      type="button"
                      disabled={controlsDisabled}
                      onClick={() => movePhoto(i, 0)}
                    >
                      Make lead
                    </MakeLeadButton>
                  )}
                  <PhotoToolRow>
                    <div>
                      <PhotoMoveBtn
                        type="button"
                        aria-label="Move photo earlier"
                        disabled={controlsDisabled || i === 0}
                        onClick={() => movePhoto(i, i - 1)}
                      >
                        ◀
                      </PhotoMoveBtn>
                      <PhotoMoveBtn
                        type="button"
                        aria-label="Move photo later"
                        disabled={controlsDisabled || i === sequence.length - 1}
                        onClick={() => movePhoto(i, i + 1)}
                      >
                        ▶
                      </PhotoMoveBtn>
                    </div>
                    {onUploadPhoto && (
                      <PhotoReplaceLabel $disabled={controlsDisabled || busyKey != null}>
                        {busyKey === i ? "Uploading…" : "Replace"}
                        <input
                          type="file"
                          accept="image/*"
                          disabled={controlsDisabled || busyKey != null}
                          onChange={(e) => {
                            replacePhoto(i, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                      </PhotoReplaceLabel>
                    )}
                  </PhotoToolRow>
                </>
              );
            }}
          />
        )}
        {renderAddPool(addPoolPhoto)}
        {onUploadPhoto && (
          <AddPhotoLabel $disabled={controlsDisabled || busyKey != null}>
            {busyKey === "add" ? "Uploading…" : "+ Add photo"}
            <input
              type="file"
              accept="image/*"
              disabled={controlsDisabled || busyKey != null}
              onChange={(e) => {
                addUploadedPhoto(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </AddPhotoLabel>
        )}
        {photoError && <PhotoErrorText>{photoError}</PhotoErrorText>}
        {[
          ["lead_image_rationale", "Lead image rationale"],
          ["sequence_rationale", "Sequence rationale"],
          ["strategy_notes", "Strategy notes"],
        ].map(([f, label]) =>
          typeof strategy[f] === "string" ? (
            <div key={f}>
              <FieldLabel htmlFor={`seq-${f}`}>{label}</FieldLabel>
              <AutoGrowTextarea
                id={`seq-${f}`}
                value={strategy[f] || ""}
                disabled={controlsDisabled}
                onChange={(e) => setStrategy({ [f]: e.target.value })}
              />
            </div>
          ) : null
        )}
        {aiBlock}
      </>
    );
  }

  // PhotoAnalysisSection — a room gallery. Photos + captions are positional,
  // so every mutation moves them together (captions padded to length first).
  const a = section.assessment || {};
  const photos = /** @type {string[]} */ (a.photos || []);
  const captions = /** @type {string[]} */ (a.photo_captions || []);
  const recommendations = /** @type {Section[]} */ (
    Array.isArray(a.recommendations) ? a.recommendations : []
  );
  /** @param {Record<string, any>} patch */
  const setAssessment = (patch) => onReplace({ ...section, assessment: { ...a, ...patch } });
  const paddedCaptions = () => {
    const next = captions.slice();
    while (next.length < photos.length) next.push("");
    return next;
  };
  /** @param {number} i */
  const removePhoto = (i) =>
    setAssessment({
      photos: photos.filter((_, j) => j !== i),
      photo_captions: paddedCaptions().filter((_, j) => j !== i),
    });
  // Photos + captions move together so a caption never detaches from its photo.
  /** @param {number} from @param {number} to */
  const reorderPhotos = (from, to) =>
    setAssessment({
      photos: arrayMove(photos, from, to),
      photo_captions: arrayMove(paddedCaptions(), from, to),
    });
  /** @param {string} name */
  const addPoolPhoto = (name) =>
    setAssessment({ photos: [...photos, name], photo_captions: [...paddedCaptions(), ""] });
  /** @param {number} i @param {File | null | undefined} file */
  const replacePhoto = async (i, file) => {
    const result = await upload(file, i);
    if (!result) return;
    const next = photos.slice();
    next[i] = result.basename;
    setAssessment({ photos: next });
  };
  /** @param {File | null | undefined} file */
  const addUploadedPhoto = async (file) => {
    const result = await upload(file, "add");
    if (result) {
      setAssessment({
        photos: [...photos, result.basename],
        photo_captions: [...paddedCaptions(), ""],
      });
    }
  };
  /** @param {number} i @param {string} v */
  const setCaption = (i, v) => {
    const next = paddedCaptions();
    next[i] = v;
    setAssessment({ photo_captions: next });
  };
  /** @param {number} i @param {string} field @param {unknown} value */
  const setRecommendation = (i, field, value) => {
    const next = recommendations.map((rec, j) => (j === i ? { ...rec, [field]: value } : rec));
    setAssessment({ recommendations: next });
  };
  // Impact/cost/effort are serialized enums ({__enum__, value}) — preserve the
  // stored shape (tagged enum vs plain string) so the worker round-trip holds.
  /** @param {number} i @param {"impact" | "cost" | "effort"} field @param {string} value */
  const setRecommendationLevel = (i, field, value) => {
    const current = recommendations[i]?.[field];
    let next;
    if (current && typeof current === "object") {
      next = { ...current, value };
    } else if (typeof current === "string") {
      next = value;
    } else {
      next = { __enum__: REC_LEVEL_ENUMS[field], value };
    }
    setRecommendation(i, field, next);
  };
  /** @param {number} i */
  const removeRecommendation = (i) =>
    setAssessment({ recommendations: recommendations.filter((_, j) => j !== i) });

  return (
    <>
      {typeof a.overview_text === "string" && (
        <div>
          <FieldLabel htmlFor={`${section.section_id}-overview`}>Overview</FieldLabel>
          <AutoGrowTextarea
            id={`${section.section_id}-overview`}
            value={a.overview_text || ""}
            disabled={controlsDisabled}
            onChange={(e) => setAssessment({ overview_text: e.target.value })}
          />
        </div>
      )}

      <FieldLabel as="div">Photos ({photos.length}) — drag to reorder</FieldLabel>
      {photos.length === 0 ? (
        <PhotosEmptyNote>No photos in this section.</PhotosEmptyNote>
      ) : (
        <SortablePhotoGrid
          items={photos}
          disabled={controlsDisabled}
          onReorder={reorderPhotos}
          container={PhotoGrid}
          itemComponent={PhotoCard}
          renderItem={(p, i) => {
            const url = photoUrl(manifest, p);
            return (
              <>
                <PhotoThumbWrap>
                  {url ? (
                    <PhotoThumb src={url} alt="" loading="lazy" draggable={false} />
                  ) : (
                    <PhotoMissing>🖼️</PhotoMissing>
                  )}
                  <PhotoRemove
                    type="button"
                    aria-label="Remove photo"
                    disabled={controlsDisabled}
                    onClick={() => removePhoto(i)}
                  >
                    ×
                  </PhotoRemove>
                </PhotoThumbWrap>
                <PhotoToolRow>
                  <div>
                    <PhotoMoveBtn
                      type="button"
                      aria-label="Move photo earlier"
                      disabled={controlsDisabled || i === 0}
                      onClick={() => reorderPhotos(i, i - 1)}
                    >
                      ◀
                    </PhotoMoveBtn>
                    <PhotoMoveBtn
                      type="button"
                      aria-label="Move photo later"
                      disabled={controlsDisabled || i === photos.length - 1}
                      onClick={() => reorderPhotos(i, i + 1)}
                    >
                      ▶
                    </PhotoMoveBtn>
                  </div>
                  {onUploadPhoto && (
                    <PhotoReplaceLabel $disabled={controlsDisabled || busyKey != null}>
                      {busyKey === i ? "Uploading…" : "Replace"}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={controlsDisabled || busyKey != null}
                        onChange={(e) => {
                          replacePhoto(i, e.target.files?.[0]);
                          e.target.value = "";
                        }}
                      />
                    </PhotoReplaceLabel>
                  )}
                </PhotoToolRow>
                <PhotoCaption
                  type="text"
                  placeholder="Add a caption…"
                  value={captions[i] || ""}
                  disabled={controlsDisabled}
                  onChange={(e) => setCaption(i, e.target.value)}
                />
              </>
            );
          }}
        />
      )}
      {onUploadPhoto && (
        <AddPhotoLabel $disabled={controlsDisabled || busyKey != null}>
          {busyKey === "add" ? "Uploading…" : "+ Add photo"}
          <input
            type="file"
            accept="image/*"
            disabled={controlsDisabled || busyKey != null}
            onChange={(e) => {
              addUploadedPhoto(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </AddPhotoLabel>
      )}
      {photoError && <PhotoErrorText>{photoError}</PhotoErrorText>}

      {renderAddPool(addPoolPhoto)}

      {recommendations.length > 0 && (
        <div>
          <FieldLabel as="div">Recommendations (shown in the document)</FieldLabel>
          {recommendations.map((rec, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: recommendations are positional and replaced wholesale on edit
            <RecommendationEditor key={i}>
              <RecommendationTop>
                <AutoGrowTextarea
                  textareaComponent={ListItemField}
                  aria-label={`Recommendation ${i + 1} title`}
                  value={rec.title || ""}
                  disabled={controlsDisabled}
                  onKeyDown={blockNewlines}
                  onChange={(e) => setRecommendation(i, "title", e.target.value)}
                />
                <IconButton
                  type="button"
                  aria-label={`Remove recommendation ${i + 1}`}
                  disabled={controlsDisabled}
                  onClick={() => removeRecommendation(i)}
                >
                  ✕
                </IconButton>
              </RecommendationTop>
              <RecommendationMeta>
                {["impact", "cost", "effort"].map((f) => {
                  const current = enumValue(rec[f]) || "Medium";
                  const options = REC_LEVELS.includes(current)
                    ? REC_LEVELS
                    : [current, ...REC_LEVELS];
                  return (
                    <MetaSelectLabel key={f}>
                      {humanizeKey(f)}
                      <MetaSelect
                        aria-label={`Recommendation ${i + 1} ${f}`}
                        value={current}
                        disabled={controlsDisabled}
                        onChange={(e) =>
                          setRecommendationLevel(
                            i,
                            /** @type {"impact" | "cost" | "effort"} */ (f),
                            e.target.value
                          )
                        }
                      >
                        {options.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </MetaSelect>
                    </MetaSelectLabel>
                  );
                })}
              </RecommendationMeta>
              <AutoGrowTextarea
                aria-label={`Recommendation ${i + 1} action`}
                value={rec.action || ""}
                disabled={controlsDisabled}
                onChange={(e) => setRecommendation(i, "action", e.target.value)}
              />
              {typeof rec.rationale === "string" && rec.rationale && (
                <AutoGrowTextarea
                  aria-label={`Recommendation ${i + 1} rationale`}
                  value={rec.rationale}
                  disabled={controlsDisabled}
                  onChange={(e) => setRecommendation(i, "rationale", e.target.value)}
                />
              )}
            </RecommendationEditor>
          ))}
        </div>
      )}

      {aiBlock}
    </>
  );
};

const RecommendationEditor = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fafafa;

  textarea {
    background: #ffffff;
    margin-bottom: 8px;
  }
`;

const RecommendationTop = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const RecommendationMeta = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin: 2px 0 8px;
`;

const MetaSelectLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
`;

const MetaSelect = styled.select`
  font-size: 0.82rem;
  font-weight: 600;
  color: #1a1a1a;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 6px 8px;
  min-height: 36px;
  background: #ffffff;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// Personalize: collapsed "finished work" summaries + one-tap approval
// ---------------------------------------------------------------------------

// (The review-priority ordering that lived here fed the old forced-approval
// "what's next" fallback; the Next link is strictly document-order now —
// Trello #17 — and the priority table went with it.)

// One-tap rewrite presets (fill + submit); the free-text field is the escape hatch.
const REWRITE_CHIPS = [
  "Make shorter",
  "Luxury tone",
  "More persuasive",
  "Seller-friendly",
  "More concise",
];

const PHOTO_COUNT_FIELDS = ["photos", "photo_order", "ordered_photos", "sequence"];

/** @param {unknown} n */
const fmtMoney = (n) => {
  const v = Number(n);
  if (!v || Number.isNaN(v)) return "";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  return `$${Math.round(v / 1000)}K`;
};

/** @param {string} t @param {number} [n] */
const clip = (t, n = 150) => {
  const s = (t || "").replace(/\s+/g, " ").trim();
  return s.length > n ? `${s.slice(0, n).replace(/\s+\S*$/, "")}…` : s;
};

const SUMMARY_TEXT_FIELDS = [
  "primary_opportunity",
  "summary",
  "brief_summary",
  "rewritten_description",
  "market_positioning",
  "overview_text",
  "conclusion_text",
  "target_buyer_appeal",
  "trend_note",
];

/** A short "finished work" line for a collapsed section — real content, so the
 * collapsed list reads as work already done, not a to-do. Pure derivation. */
/** @param {Section} section */
const sectionSummary = (section) => {
  const t = section.__type__;
  if (t === "ComparableAnalysisSection") {
    const parts = [];
    const n = (section.comparable_properties || []).length;
    if (n) parts.push(`${n} comp${n === 1 ? "" : "s"}`);
    if (section.suggested_range_low && section.suggested_range_high) {
      parts.push(
        `${fmtMoney(section.suggested_range_low)}–${fmtMoney(section.suggested_range_high)}`
      );
    }
    if (section.pricing_verdict) parts.push(String(section.pricing_verdict).toLowerCase());
    return parts.join(" · ") || "Pricing & comps ready";
  }
  if (t === "TaxRecordsSection") {
    const n = (section.records || []).length;
    return n ? `${n} year${n === 1 ? "" : "s"} of tax history on record` : "Tax history ready";
  }
  if (t === "PriceHistorySection") {
    const n = (section.records || []).length;
    return n ? `${n} price event${n === 1 ? "" : "s"} on record` : "Price history ready";
  }
  if (PHOTO_SECTION_TYPES.has(t)) {
    // Photos live under assessment.photos (room sections) or
    // strategy.complete_sequence (the sequence) — not at the top level.
    const nested = [
      section.assessment?.photos,
      section.strategy?.complete_sequence,
      ...PHOTO_COUNT_FIELDS.map((f) => section[f]),
    ].find((v) => Array.isArray(v) && v.length);
    // Say that the controls are in here. Adding and reordering photos lives
    // inside this section, and a collapsed summary that only reported finished
    // work left an agent concluding there was no way to add photos at all
    // (Trello #74).
    if (nested) {
      const count = `${nested.length} photo${nested.length === 1 ? "" : "s"} with direction`;
      return `${count} · open to reorder, replace or add photos`;
    }
    return "Photo direction ready · open to reorder, replace or add photos";
  }
  for (const f of SUMMARY_TEXT_FIELDS) {
    if (typeof section[f] === "string" && section[f].trim()) return clip(section[f]);
  }
  for (const f of ["key_assets", "key_highlights", "neighborhood_highlights", "likely_outcomes"]) {
    if (Array.isArray(section[f]) && section[f].length)
      return clip(section[f].slice(0, 2).join(" · "));
  }
  return "Ready to review.";
};

const SummaryText = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #4b5563;
  margin: 8px 0 0;
`;

const StatusTick = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: #0b605d;
  white-space: nowrap;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 16px;
`;

// The single high-emphasis action used to live here; approvals are now
// treated as complete by default and the explicit approval button removed.

// Edit / Rewrite — deliberately one level down (quiet).
const QuietButton = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: ${(p) => (p.$on ? "#0b605d" : "#374151")};

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0;
`;

const Chip = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #bfe0dc;
  background: #f0faf9;
  color: #0b605d;

  &:hover:not(:disabled) {
    background: #dff2ef;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// "What changed" before/after view for an AI rewrite.
const RewrittenBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  border-radius: 10px;
  padding: 8px 12px;
  margin: 10px 0 4px;
  font-size: 0.85rem;
  color: #5b21b6;
  font-weight: 600;
`;

const WhatChangedButton = styled.button`
  background: none;
  border: none;
  color: #6d28d9;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
  min-height: 36px;
`;

const DiffBox = styled.div`
  margin: 10px 0 4px;
`;

const DiffPane = styled.div`
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 8px;
  background: ${(p) => (p.$after ? "#f0fdf4" : "#fef2f2")};
  border: 1px solid ${(p) => (p.$after ? "#bbf7d0" : "#fecaca")};
  color: #1a1a1a;
`;

const DiffTag = styled.div`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => (p.$after ? "#166534" : "#991b1b")};
  margin-bottom: 4px;
`;

/** The primary prose field an AI rewrite changed (for the before/after view). */
/** @param {Section} section @param {Section | null | undefined} before @param {{text?: string[]} | undefined} config */
const primaryChange = (section, before, config) => {
  if (!before) return null;
  for (const f of config?.text || []) {
    if (
      typeof section[f] === "string" &&
      typeof before[f] === "string" &&
      section[f] !== before[f]
    ) {
      return { field: f, before: before[f], after: section[f] };
    }
  }
  return null;
};

/**
 * @param {{
 * section: Section, index: number, total: number,
 * onField: (field: string, value: string) => void,
 * onList: (field: string, items: string[]) => void, onMove: (dir: -1 | 1) => void,
 * onReplace: (section: Section) => void, onRemove: () => void,
 * onRevise: (instruction: string) => void, isRevising: boolean,
 * onUpdateComps?: (addresses: string[], excluded: string[]) => void,
 * isUpdatingComps?: boolean, compsWarning?: string | null, subjectDetails?: Section,
 * onUpdatePhotos?: (photos: string[]) => void, isUpdatingPhotos?: boolean,
 * photosChanged?: boolean, photoManifest: Record<string, string>, disabled: boolean,
 * actionsDisabled?: boolean,
 * approved: boolean, onApprove?: (value: boolean) => void, onUndoRevise?: () => void,
 * defaultExpanded?: boolean, readOnly?: boolean,
 * anchorId: string, onSwipe?: (dir: -1 | 1) => void, diffBefore?: Section,
 * onActivate?: () => void, expandRequest?: {id: string, token: number} | null,
 * onUploadPhoto?: (file: File) => Promise<{basename: string, url: string} | null>,
 * }} props
 */

const SectionEditor = ({
  section,
  index,
  total,
  onMove,
  onField,
  onList,
  onReplace,
  onRemove,
  onRevise,
  isRevising,
  onUpdateComps,
  isUpdatingComps,
  compsWarning,
  subjectDetails,
  onUpdatePhotos,
  isUpdatingPhotos,
  photosChanged,
  photoManifest,
  disabled,
  actionsDisabled = false,
  approved,
  onUndoRevise,
  defaultExpanded,
  readOnly,
  anchorId,
  onSwipe,
  diffBefore,
  onActivate,
  expandRequest,
  onUploadPhoto,
}) => {
  const config = SECTION_FIELDS[section.__type__];
  const textFields = (config?.text || []).filter((f) => typeof section[f] === "string");
  const listFields = (config?.lists || []).filter((f) => Array.isArray(section[f]));
  const isPhotoSection = PHOTO_SECTION_TYPES.has(section.__type__);
  const hasEditableFields = textFields.length > 0 || listFields.length > 0 || isPhotoSection;
  const revisable = REVISABLE_TYPES.has(section.__type__);
  // collapsed = finished-work summary; edit = the field editors; rewrite = AI chips.
  // Read-only viewers always see the content (edit layout, disabled).
  const [mode, setMode] = useState(readOnly || defaultExpanded ? "edit" : "collapsed");
  const [instruction, setInstruction] = useState("");
  const [showDiff, setShowDiff] = useState(false);
  const touchRef = useRef({ x: 0, y: 0 });

  // Preview → editor navigation: an external request (clicking this section's
  // heading in the live preview) opens the field editors.
  useEffect(() => {
    if (expandRequest && expandRequest.id === section.section_id && !readOnly) {
      setMode("edit");
    }
  }, [expandRequest, section.section_id, readOnly]);

  /** @param {"edit" | "collapsed" | "rewrite"} next */
  const switchMode = (next) => {
    setMode(next);
    // Editor → preview focus-sync: opening a section for editing/rewriting is
    // the "I'm working here" signal the preview follows.
    if (next !== "collapsed" && onActivate) onActivate();
  };

  const badge = config?.locked
    ? { label: "Numbers locked", $bg: "#fffbeb", $fg: "#92400e", $bd: "#fde68a" }
    : null;
  const change = primaryChange(section, diffBefore, config);

  /** @param {string} [preset] */
  const submitRevise = (preset) => {
    const text = (preset ?? instruction).trim();
    if (!text || disabled || actionsDisabled || isRevising) return;
    onRevise(text);
    setInstruction("");
    setMode("collapsed");
  };

  /** @param {React.TouchEvent} e */
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  /** @param {React.TouchEvent} e */
  const onTouchEnd = (e) => {
    if (!onSwipe) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    // A deliberate horizontal swipe moves to the next/previous section.
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) onSwipe(dx < 0 ? 1 : -1);
  };

  return (
    <SectionCard
      id={anchorId}
      $approved={approved && mode === "collapsed"}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <SectionHeader>
        <SectionTitleGroup>
          <SectionName>{sectionTitle(section)}</SectionName>
          {(isRevising ||
            isUpdatingPhotos ||
            (section.__type__ === "ComparableAnalysisSection" && isUpdatingComps)) && (
            <SectionSpinner aria-label="AI is rewriting this section" role="status" />
          )}
          {approved && !readOnly ? (
            <StatusTick>✓ Complete</StatusTick>
          ) : (
            badge && (
              <AiBadge $bg={badge.$bg} $fg={badge.$fg} $bd={badge.$bd}>
                {badge.label}
              </AiBadge>
            )
          )}
        </SectionTitleGroup>
        {mode === "edit" && !readOnly && (
          <SectionControls>
            <IconButton
              type="button"
              aria-label="Move up"
              disabled={index === 0 || disabled}
              onClick={() => onMove(-1)}
            >
              ↑
            </IconButton>
            <IconButton
              type="button"
              aria-label="Move down"
              disabled={index === total - 1 || disabled}
              onClick={() => onMove(1)}
            >
              ↓
            </IconButton>
            <RemoveButton type="button" disabled={disabled} onClick={onRemove}>
              Remove
            </RemoveButton>
          </SectionControls>
        )}
      </SectionHeader>

      {change && !readOnly && (
        <>
          <RewrittenBar>
            <span>✨ Rewritten by AI</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <WhatChangedButton type="button" onClick={() => setShowDiff((v) => !v)}>
                {showDiff ? "Hide changes" : "See what changed"}
              </WhatChangedButton>
              {diffBefore && onUndoRevise && (
                <QuietButton type="button" onClick={() => onUndoRevise()}>
                  Undo
                </QuietButton>
              )}
            </div>
          </RewrittenBar>
          {showDiff && (
            <DiffBox>
              <DiffPane>
                <DiffTag>Before</DiffTag>
                {change.before || "—"}
              </DiffPane>
              <DiffPane $after>
                <DiffTag $after>After</DiffTag>
                {change.after || "—"}
              </DiffPane>
            </DiffBox>
          )}
        </>
      )}

      {mode === "collapsed" && <SummaryText>{sectionSummary(section)}</SummaryText>}

      {mode === "edit" && (
        <>
          {section.__type__ === "ComparableAnalysisSection" && onUpdateComps && (
            <CompsEditor
              section={section}
              subject={subjectDetails}
              disabled={disabled || actionsDisabled}
              isUpdating={!!isUpdatingComps}
              warning={compsWarning}
              onUpdate={onUpdateComps}
              onReplaceSection={onReplace}
            />
          )}

          {textFields.map((field) => (
            <div key={field}>
              <FieldLabel htmlFor={`${section.__type__}-${field}`}>{humanizeKey(field)}</FieldLabel>
              <AutoGrowTextarea
                id={`${section.__type__}-${field}`}
                value={section[field] || ""}
                onChange={(e) => onField(field, e.target.value)}
                disabled={disabled}
              />
            </div>
          ))}

          {listFields.map((field) => (
            <div key={field}>
              <FieldLabel>{humanizeKey(field)}</FieldLabel>
              {section[field].map((/** @type {string} */ item, /** @type {number} */ i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: free-text list items have no stable id; the whole array is replaced on every edit
                <ListRow key={i}>
                  <AutoGrowTextarea
                    textareaComponent={ListItemField}
                    value={item}
                    disabled={disabled}
                    onKeyDown={blockNewlines}
                    onChange={(e) => {
                      const next = section[field].slice();
                      next[i] = e.target.value;
                      onList(field, next);
                    }}
                  />
                  <IconButton
                    type="button"
                    aria-label="Remove item"
                    disabled={disabled}
                    onClick={() =>
                      onList(
                        field,
                        section[field].filter(
                          (/** @type {string} */ _, /** @type {number} */ j) => j !== i
                        )
                      )
                    }
                  >
                    ✕
                  </IconButton>
                </ListRow>
              ))}
              <AddButton
                type="button"
                disabled={disabled}
                onClick={() => onList(field, [...section[field], ""])}
              >
                + Add
              </AddButton>
            </div>
          ))}

          {isPhotoSection && (
            <PhotoSectionEditor
              section={section}
              manifest={photoManifest || {}}
              onReplace={onReplace}
              disabled={disabled || actionsDisabled}
              onUpdatePhotos={onUpdatePhotos}
              isUpdating={!!isUpdatingPhotos}
              photosChanged={!!photosChanged}
              onUploadPhoto={onUploadPhoto}
            />
          )}

          {config?.locked && <LockedNote>{config.locked}</LockedNote>}
          {!hasEditableFields && !config?.locked && !isPhotoSection && (
            <ReadOnlyNote>
              This part is built from your photos and isn't text-editable here yet.
            </ReadOnlyNote>
          )}
        </>
      )}

      {mode === "rewrite" && revisable && (
        <ReviseBox>
          <ReviseLabel>
            Rewrite with AI — tap a direction, or type your own · always free
          </ReviseLabel>
          <ChipRow>
            {REWRITE_CHIPS.map((chip) => (
              <Chip
                key={chip}
                type="button"
                disabled={disabled || actionsDisabled || isRevising}
                onClick={() => submitRevise(chip)}
              >
                {chip}
              </Chip>
            ))}
          </ChipRow>
          <ReviseRow>
            <ReviseInput
              type="text"
              placeholder='e.g. "mention the new roof" or "sound less salesy"'
              value={instruction}
              disabled={disabled || actionsDisabled || isRevising}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRevise();
              }}
            />
            <ReviseButton
              type="button"
              onClick={() => submitRevise()}
              disabled={disabled || actionsDisabled || isRevising || !instruction.trim()}
            >
              {isRevising ? "Improving…" : "Improve"}
            </ReviseButton>
          </ReviseRow>
        </ReviseBox>
      )}

      {!readOnly && (
        <ActionRow>
          {hasEditableFields && (
            <QuietButton
              type="button"
              $on={mode === "edit"}
              onClick={() => switchMode(mode === "edit" ? "collapsed" : "edit")}
            >
              {mode === "edit" ? "Done" : "Edit"}
            </QuietButton>
          )}
          {revisable && (
            <QuietButton
              type="button"
              $on={mode === "rewrite"}
              disabled={disabled || actionsDisabled}
              onClick={() => switchMode(mode === "rewrite" ? "collapsed" : "rewrite")}
            >
              Improve
            </QuietButton>
          )}
        </ActionRow>
      )}
    </SectionCard>
  );
};

/** "a", "a and b", "a, b, and c" */
const joinList = (/** @type {string[]} */ items) => {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

/**
 * Pricing v2 ready banner: only what was actually rendered, read from the
 * artifact manifest — never a promise of the whole bundle (v3: nothing is
 * rendered speculatively). One orientation line; no watermark copy here (the
 * two mentions are the export moment and the account page).
 * @param {Record<string, any>} doc
 */
const creditsReadyBannerText = (doc) => {
  const artifacts = doc.artifacts || {};
  /** @type {string[]} */
  const prepared = [];
  if (artifactUsable(artifacts.report_pdf, "report_pdf")) prepared.push("Branded PDF");
  const deckReady = artifactUsable(artifacts.deck, "deck");
  const pptxReady = artifactUsable(artifacts.pptx, "pptx");
  if (deckReady || pptxReady) {
    prepared.push(
      deckReady && pptxReady
        ? "Seller Presentation (PDF and PowerPoint)"
        : deckReady
          ? "Seller Presentation (PDF)"
          : "Seller Presentation (PowerPoint)"
    );
  }
  if (artifactUsable(artifacts.one_pager, "one_pager")) prepared.push("one-pager");
  if (Array.isArray(doc.social_posts) && doc.social_posts.length > 0) {
    prepared.push("social captions");
  }
  const base =
    "Your listing prep is ready — the document in the preview is the deliverable, and it's shareable on the web.";
  const files = prepared.length > 0 ? ` Also prepared: ${joinList(prepared)}.` : "";
  return `${base}${files} Personalize and Share are always free.`;
};

// Advanced tools (regenerate, branding) — collapsed by default.
const AdvancedToggle = styled.button`
  width: 100%;
  text-align: left;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 14px 16px;
  margin: 8px 0 18px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #4b5563;
  cursor: pointer;
  min-height: 44px;

  &:hover {
    background: #f9fafb;
  }
`;

const RestorePanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 4px 0 16px;
  padding: 12px 14px;
  border: 1px solid #d5ece9;
  border-radius: 12px;
  background: #f3faf9;
  color: #374151;
  font-size: 0.86rem;
`;

const RestoreActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const RestoreButton = styled.button`
  min-height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #9dcfca;
  background: #ffffff;
  color: #0b605d;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #e8f5f3;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Sticky bottom bar progress + Continue-to-Share.
const ProgressWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProgressText = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const NextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0e7e7a;
  cursor: pointer;
  text-decoration: underline;
`;

const ContinueButton = styled.button`
  min-height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #0b605d;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const ReportEditorPage = () => {
  /** @type {{ uuid: string }} */
  const { uuid } = useParams();
  const history = useHistory();
  const location = useLocation();
  // Deep link from the property space: ?open=photos | ?open=reprepare.
  const openParam = new URLSearchParams(location.search).get("open");
  const [doc, setDoc] = useState(/** @type {Record<string, any> | null} */ (null));
  const documentVersionRef = useRef(0);
  // An old response must never apply to a different report or an unmounted editor.
  const editorScope = useMemo(() => ({ uuid }), [uuid]);
  const editorScopeRef = useRef(/** @type {{ uuid: string } | null} */ (editorScope));
  const loadedScopeRef = useRef(/** @type {{ uuid: string } | null} */ (null));
  const loadedTokenRef = useRef(/** @type {string | null} */ (null));
  editorScopeRef.current = editorScope;
  useEffect(() => {
    editorScopeRef.current = editorScope;
    return () => {
      if (editorScopeRef.current === editorScope) editorScopeRef.current = null;
    };
  }, [editorScope]);
  const [sections, setSections] = useState(/** @type {Section[]} */ ([]));
  const [status, setStatus] = useState(
    /** @type {"loading" | "ok" | "not_found" | "error"} */ ("loading")
  );
  const [saveStatus, setSaveStatus] = useState(
    /** @type {"idle" | "saving" | "saved" | "rendering" | "ready" | "error"} */ ("idle")
  );
  const [activeReportJobId, setActiveReportJobId] = useState(/** @type {string | null} */ (null));
  const [revisingSectionId, setRevisingSectionId] = useState(/** @type {string | null} */ (null));
  const [reviseBaseVersion, setReviseBaseVersion] = useState(/** @type {number | null} */ (null));
  const [branding, setBranding] = useState(/** @type {Record<string, any>} */ ({}));
  const [videoBetaEligible, setVideoBetaEligible] = useState(false);
  const [logoError, setLogoError] = useState(/** @type {string | null} */ (null));
  const [editable, setEditable] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenBaseVersion, setRegenBaseVersion] = useState(/** @type {number | null} */ (null));
  const [regenGuidance, setRegenGuidance] = useState("");
  // Pricing v2. `creditsBlock` is a server 409 on the balance — the paywall
  // sheet opens in place; `reprepareConfirm` is the re-prepare spend sheet.
  const account = useCreditsAccount();
  const accountRefresh = account.refresh;
  const [creditsBlock, setCreditsBlock] = useState(
    /** @type {{balance: number, cost: number, action: string} | null} */ (null)
  );
  const [reprepareConfirm, setReprepareConfirm] = useState(false);
  const reprepareButtonRef = useRef(/** @type {HTMLButtonElement|null} */ (null));
  const reprepare = doc?.reprepare;
  // The owner payload carries the re-prepare quote only for credits listings.
  const reprepareQuote = useMemo(() => {
    if (!reprepare || typeof reprepare !== "object") return null;
    const cost = Number(reprepare.cost);
    if (!Number.isFinite(cost) || cost < 0) return null;
    return { cost, free: reprepare.free_rerun_available === true || cost === 0 };
  }, [reprepare]);
  // A bought file or the social set landed: adopt the payload's file-shaped
  // fields without touching the sections being edited here.
  const mergeDocMeta = useCallback((/** @type {Record<string, any>} */ fresh) => {
    setDoc((current) => {
      if (!current) return current;
      const {
        artifacts,
        export_delivery,
        addable,
        social_posts,
        watermarked,
        photo_touchups,
        reprepare: quote,
        report_pdf_url,
        one_pager_pdf_url,
        deck_pdf_url,
        pptx_url,
        rendered_version,
      } = fresh;
      const picked = {
        artifacts,
        export_delivery,
        addable,
        social_posts,
        watermarked,
        photo_touchups,
        reprepare: quote,
        report_pdf_url,
        one_pager_pdf_url,
        deck_pdf_url,
        pptx_url,
        rendered_version,
      };
      const next = { ...current };
      for (const [key, value] of Object.entries(picked)) {
        if (value !== undefined) next[key] = value;
      }
      return next;
    });
  }, []);
  // The comps-update flow: one AI pass rebuilds the comparable analysis around
  // the agent's hand-picked addresses. `requestedCompsRef` remembers what was
  // asked for so we can warn when the AI couldn't verify an address.
  const [updatingComps, setUpdatingComps] = useState(false);
  const [compsBaseVersion, setCompsBaseVersion] = useState(/** @type {number | null} */ (null));
  const [compsWarning, setCompsWarning] = useState(/** @type {string | null} */ (null));
  const requestedCompsRef = useRef(/** @type {string[]} */ ([]));
  // The photos-update flow (one photo section rebuilt around the agent's
  // curated set/order). Baselines remember each photo section's server-side
  // photo list so the editor can flag "photos changed, write-up may be stale".
  const [updatingPhotosSectionId, setUpdatingPhotosSectionId] = useState(
    /** @type {string | null} */ (null)
  );
  const [photosBaseVersion, setPhotosBaseVersion] = useState(/** @type {number | null} */ (null));
  const photoBaselineRef = useRef(/** @type {Record<string, string>} */ ({}));
  /** @param {Section[]} secs */
  const resetPhotoBaselines = useCallback((/** @type {Section[]} */ secs) => {
    /** @type {Record<string, string>} */
    const map = {};
    for (const s of secs || []) {
      if (PHOTO_SECTION_TYPES.has(s.__type__)) {
        map[s.section_id] = sectionPhotoBasenames(s).join("|");
      }
    }
    photoBaselineRef.current = map;
  }, []);
  // Success flash for the AI pill: set when a poll lands the refreshed
  // content, auto-cleared a few seconds later.
  const [aiDoneMessage, setAiDoneMessage] = useState(/** @type {string | null} */ (null));
  const aiDoneTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  /** @param {string} message */
  const flashAiDone = useCallback((/** @type {string} */ message) => {
    setAiDoneMessage(message);
    if (aiDoneTimerRef.current) clearTimeout(aiDoneTimerRef.current);
    aiDoneTimerRef.current = setTimeout(() => setAiDoneMessage(null), 4000);
  }, []);
  useEffect(
    () => () => {
      if (aiDoneTimerRef.current) clearTimeout(aiDoneTimerRef.current);
    },
    []
  );

  // Mobile Edit/Preview tab. Starts on Preview for a fresh, never-edited draft
  // (the "look, it's done" moment), on Edit once personalizing has begun.
  const [mobileView, setMobileView] = useState(/** @type {"edit" | "preview" | null} */ (null));
  // Focus-sync: the preview follows the section being worked on (never a
  // continuous scroll-lock — the panes' heights don't correspond). Desktop
  // scrolls the preview rail live; mobile remembers the active section and
  // jumps there when the Preview tab opens. `previewHighlightId` drives the
  // fading accent ring on the synced section.
  const previewRailRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const activeSectionRef = useRef(/** @type {string | null} */ (null));
  const [activeSectionId, setActiveSectionId] = useState(/** @type {string | null} */ (null));
  const lastSyncedRef = useRef(/** @type {string | null} */ (null));
  const [previewHighlightId, setPreviewHighlightId] = useState(/** @type {string | null} */ (null));
  const highlightTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  // Preview → editor: bumping this asks the matching section card to expand.
  const [expandRequest, setExpandRequest] = useState(
    /** @type {{ id: string, token: number } | null} */ (null)
  );
  // Scroll-walking pauses while WE are scrolling the page (preview-heading
  // click, "Next:" link, approval auto-advance) — otherwise the spy would walk
  // the preview through every section the smooth scroll passes.
  const spySuppressUntilRef = useRef(0);
  // ...and the mirror for the preview pane: its scrollspy pauses while WE are
  // scrolling the preview programmatically (focus-sync, AI-update jumps), so
  // the two spies can never ping-pong each other.
  const previewSpySuppressUntilRef = useRef(0);
  // A specific, friendly error message (e.g. session expired / not the owner)
  // that overrides the generic "something went wrong" in the save bar.
  const [errorDetail, setErrorDetail] = useState(/** @type {string | null} */ (null));
  // Per-section "Looks good" approvals (client-side review progress), and whether
  // there are unsaved edits pending the debounced auto-save.
  const [approvals, setApprovals] = useState(/** @type {Record<string, boolean>} */ ({}));
  const [dirty, setDirty] = useState(false);
  const dirtyRevisionRef = useRef(0);
  const dirtyRef = useRef(false);
  const latestDraftRef = useRef({ doc, sections, branding, editable });
  latestDraftRef.current = { doc, sections, branding, editable };
  const saveFlightRef = useRef(
    /** @type {{ scope: { uuid: string }, promise: Promise<boolean> } | null} */ (null)
  );
  const shareFlightRef = useRef(/** @type {{ uuid: string } | null} */ (null));
  const [continuingToShare, setContinuingToShare] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState(
    /** @type {import("../../utils/reportDrafts").ReportDraft | null} */ (null)
  );
  const draftBackupRef = useRef(
    /** @type {import("../../utils/reportDrafts").ReportDraft | null} */ (null)
  );
  const [draftBackupFailed, setDraftBackupFailed] = useState(false);
  const loadedDraftRef = useRef(
    /** @type {{ sections: Section[], branding: Record<string, any> } | null} */ (null)
  );
  const [removedSections, setRemovedSections] = useState(
    /** @type {{ section: Section, index: number }[]} */ ([])
  );
  const [historyVersions, setHistoryVersions] = useState(/** @type {any[]} */ ([]));
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [restoreBaseVersion, setRestoreBaseVersion] = useState(/** @type {number | null} */ (null));
  const [feedbackKind, setFeedbackKind] = useState("factual_error");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState(
    /** @type {"idle" | "sending" | "sent" | "error"} */ ("idle")
  );
  const markDirty = useCallback(() => {
    dirtyRevisionRef.current += 1;
    dirtyRef.current = true;
    setDirty(true);
  }, []);
  // Advanced tools (regenerate, branding) collapse behind progressive disclosure —
  // the default interaction is approval, not full editing.
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Before/after snapshots for AI rewrites: section_id -> the pre-rewrite section,
  // so the editor can show what changed. Captured on revise, applied on reload.
  const [revisedBefore, setRevisedBefore] = useState(/** @type {Record<string, any>} */ ({}));
  const pendingReviseRef = useRef(/** @type {{ id: string, before: any } | null} */ (null));
  // "Resume where left off": jump to the next unreviewed section on reopen, once.
  const resumedRef = useRef(false);
  // Analytics: when this Personalize session started, and which sections the
  // agent actually touched (hand-edit or AI rewrite) — powers the brief's
  // success metrics (review time, share of sections edited).
  const openedAtRef = useRef(0);
  const editedSectionsRef = useRef(/** @type {Set<string>} */ (new Set()));

  /**
   * Every editor behavior event is pinned to the report and exact document
   * version that the agent saw when taking the action.
   * @param {string} event
   * @param {Record<string, string | number | boolean>} [properties]
   */
  const trackPersonalize = useCallback(
    (
      /** @type {string} */ event,
      /** @type {Record<string, string | number | boolean>} */ properties = {}
    ) => {
      track(event, {
        ...properties,
        computation_uuid: uuid,
        document_version: documentVersionRef.current,
      });
    },
    [uuid]
  );

  /**
   * Apply a version returned by an edit/job-start response immediately. This
   * keeps retries on the server-committed base even before the worker finishes.
   * @param {Record<string, any> | null | undefined} data
   * @param {Record<string, any>} [persisted]
   */
  const applyResponseVersion = useCallback(
    (
      /** @type {Record<string, any> | null | undefined} */ data,
      /** @type {Record<string, any>} */ persisted = {}
    ) => {
      const version = reportVersion(data?.version);
      if (version == null) return null;
      const renderedVersion = reportVersion(data?.rendered_version);
      documentVersionRef.current = version;
      setDoc((current) =>
        current
          ? {
              ...current,
              ...persisted,
              version,
              ...(renderedVersion == null ? {} : { rendered_version: renderedVersion }),
            }
          : current
      );
      return version;
    },
    []
  );

  /** @param {string} sectionId @param {string} sectionType */
  const markSectionTouched = useCallback(
    (/** @type {string} */ sectionId, /** @type {string} */ sectionType) => {
      if (!sectionId || editedSectionsRef.current.has(sectionId)) return;
      editedSectionsRef.current.add(sectionId);
      trackPersonalize("personalize_section_edited", {
        section_type: sectionType || "unknown",
      });
    },
    [trackPersonalize]
  );

  // A content change invalidates that section's approval (it's no longer the
  // thing the agent blessed).
  /** @param {string | null | undefined} sectionId */
  const unapproveSection = useCallback(
    (/** @type {string | null | undefined} */ sectionId) => {
      if (!sectionId) return;
      setApprovals((prev) => {
        if (!prev[sectionId]) return prev;
        const next = { ...prev };
        delete next[sectionId];
        saveApprovals(uuid, next);
        return next;
      });
    },
    [uuid]
  );
  const clearAllApprovals = useCallback(() => {
    setApprovals({});
    saveApprovals(uuid, {});
  }, [uuid]);

  const fetchDoc = useCallback(async () => {
    const loadToken = getToken();
    loadedScopeRef.current = null;
    loadedTokenRef.current = null;
    dirtyRef.current = false;
    draftBackupRef.current = null;
    setDirty(false);
    setEditable(false);
    setRecoveredDraft(null);
    setDraftBackupFailed(false);
    setSaveStatus("idle");
    setContinuingToShare(false);
    setStatus("loading");
    try {
      const resp = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
        headers: authHeader(),
      });
      if (editorScopeRef.current !== editorScope || getToken() !== loadToken) return;
      setDoc(resp.data);
      documentVersionRef.current = reportVersion(resp.data.version) || 0;
      setSections(resp.data.content?.sections || []);
      resetPhotoBaselines(resp.data.content?.sections || []);
      let nextBranding = resp.data.branding || {};
      setVideoBetaEligible(false);
      // Editor default: fill any blank branding fields from the agent's account
      // profile, so editing a report that predates account branding still starts
      // from their saved broker info. Only fills blanks — never overrides what
      // the report already carries.
      if (getToken()) {
        try {
          const me = await axios.get(`${API_URL}/auth/me/`, { headers: authHeader() });
          if (editorScopeRef.current !== editorScope) return;
          setVideoBetaEligible(me.data?.beta?.access === true);
          const acct = me.data?.branding || {};
          const defaults = {
            agent_name: acct.name,
            brokerage: acct.brokerage,
            accent_color: acct.accent_color,
            logo_url: acct.logo_url,
            headshot_url: acct.headshot_url,
          };
          const merged = { ...nextBranding };
          for (const [k, v] of Object.entries(defaults)) {
            if (v && !merged[k]) merged[k] = v;
          }
          // Booleans can't use the truthy-fill above — only inherit the account
          // default when the report hasn't set its own preference.
          if (merged.show_logo === undefined && acct.show_logo !== undefined) {
            merged.show_logo = acct.show_logo;
          }
          if (merged.show_headshot === undefined && acct.show_headshot !== undefined) {
            merged.show_headshot = acct.show_headshot;
          }
          nextBranding = merged;
        } catch {
          // Not signed in or no account branding — keep the report's own branding.
        }
      }
      if (editorScopeRef.current !== editorScope || getToken() !== loadToken) return;
      setBranding(nextBranding);
      loadedScopeRef.current = editorScope;
      loadedTokenRef.current = loadToken;
      // Never reveal a stored report draft until this GET proves edit ownership.
      const candidate = resp.data.editable ? loadReportDraft(uuid) : null;
      const alreadySaved =
        candidate &&
        JSON.stringify(candidate.content) === JSON.stringify(resp.data.content) &&
        JSON.stringify(candidate.branding) === JSON.stringify(nextBranding);
      // A lost response can leave a backup of edits that already reached the server.
      // Exact equality is conservative; never dismiss a different/newer backup.
      if (alreadySaved) {
        clearReportDraft(uuid, candidate.id);
        const remaining = loadReportDraft(uuid);
        setRecoveredDraft(remaining?.id === candidate.id ? null : remaining);
      } else {
        setRecoveredDraft(candidate);
      }
      loadedDraftRef.current = {
        sections: cloneJson(resp.data.content?.sections || []),
        branding: cloneJson(nextBranding) || {},
      };
      setRemovedSections([]);
      dirtyRevisionRef.current = 0;
      setEditable(!!resp.data.editable);
      // By default treat every section as complete so the editor reads like
      // a finished deliverable. Preserve any previously stored approvals
      // (they're a subset) but default any missing section to approved.
      const storedApprovals = loadApprovals(uuid);
      const secs = resp.data.content?.sections || [];
      /** @type {Record<string, boolean>} */
      const defaultApprovals = {};
      for (const s of secs) {
        if (s?.section_id) defaultApprovals[s.section_id] = true;
      }
      setApprovals({ ...defaultApprovals, ...storedApprovals });
      setDirty(false);
      setStatus("ok");
      // Start the Personalize session clock (owners only — viewers don't review).
      if (resp.data.editable && !openedAtRef.current) {
        openedAtRef.current = Date.now();
        track("personalize_opened", {
          computation_uuid: uuid,
          document_version: resp.data.version || 0,
          sections_total: (resp.data.content?.sections || []).length,
        });
      }
    } catch (err) {
      if (editorScopeRef.current !== editorScope) return;
      const axiosErr = /** @type {{ response?: { status?: number } }} */ (err);
      setStatus(axiosErr.response?.status === 404 ? "not_found" : "error");
      if (axiosErr.response?.status !== 404) console.error("Report document fetch failed:", err);
    }
  }, [uuid, editorScope, resetPhotoBaselines]);

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  /** @param {number} index @param {string} field @param {string} value */
  const updateField = useCallback(
    (/** @type {number} */ index, /** @type {string} */ field, /** @type {string} */ value) => {
      setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
      markDirty();
    },
    [markDirty]
  );

  // Upload a photo for replace/add in the photo sections. The server stores it,
  // registers it in the document's photo_manifest, and returns {basename, url};
  // merging the pair into the local manifest makes the new thumbnail resolve
  // immediately, before any save round-trip.
  const uploadPrepPhoto = useCallback(
    /** @param {File} file */
    async (file) => {
      try {
        const form = new FormData();
        form.append("image", file);
        const resp = await axios.post(
          `${API_URL}/computations/${uuid}/report-document/photos/`,
          form,
          { headers: authHeader() }
        );
        const { basename, url } = resp.data || {};
        if (!basename || !url) return null;
        setDoc((d) =>
          d ? { ...d, photo_manifest: { ...(d.photo_manifest || {}), [basename]: url } } : d
        );
        // The server commits the manifest merge, which BUMPS the document
        // version. Ignoring it left doc.version stale, so the next autosave
        // sent a stale expected_version and 409ed — permanently, because the
        // retry re-sent the same stale value. Adopt the returned version.
        applyResponseVersion(resp.data);
        return { basename, url };
      } catch (err) {
        const axiosErr = /** @type {{ response?: { data?: any }, message?: string }} */ (err);
        console.error("Prep photo upload failed:", axiosErr.response?.data || axiosErr.message);
        return null;
      }
    },
    [uuid, applyResponseVersion]
  );

  // Replace a whole section object — used by the photo editor, whose edits live
  // in nested structures (assessment / strategy) the flat field setters can't reach.
  /** @param {number} index @param {Section} next */
  const replaceSection = useCallback(
    (/** @type {number} */ index, /** @type {Section} */ next) => {
      setSections((prev) => prev.map((s, i) => (i === index ? next : s)));
      markDirty();
    },
    [markDirty]
  );

  /** @param {number} index @param {string} field @param {string[]} items */
  const updateList = useCallback(
    (/** @type {number} */ index, /** @type {string} */ field, /** @type {string[]} */ items) => {
      setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: items } : s)));
      markDirty();
    },
    [markDirty]
  );

  /** @param {number} index @param {-1 | 1} dir */
  const moveSection = useCallback(
    (/** @type {number} */ index, /** @type {-1 | 1} */ dir) => {
      setSections((prev) => {
        const target = index + dir;
        if (target < 0 || target >= prev.length) return prev;
        const next = prev.slice();
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      markDirty();
    },
    [markDirty]
  );

  /** @param {number} index */
  const removeSection = useCallback(
    (/** @type {number} */ index) => {
      const removed = sections[index];
      if (!removed) return;
      setSections((prev) => prev.filter((_, i) => i !== index));
      setRemovedSections((prev) => [...prev, { section: cloneJson(removed), index }]);
      markDirty();
    },
    [sections, markDirty]
  );

  /** @param {number} removedIndex */
  const restoreRemovedSection = useCallback(
    (/** @type {number} */ removedIndex) => {
      const removed = removedSections[removedIndex];
      if (!removed) return;
      setSections((prev) => {
        const next = prev.slice();
        next.splice(Math.min(removed.index, next.length), 0, cloneJson(removed.section));
        return next;
      });
      setRemovedSections((prev) => prev.filter((_, i) => i !== removedIndex));
      markDirty();
    },
    [removedSections, markDirty]
  );

  const resetToLoadedDraft = useCallback(() => {
    const baseline = loadedDraftRef.current;
    if (!baseline) return;
    if (!window.confirm("Reset every section and branding change to the draft you opened?")) return;
    trackPersonalize("personalize_section_reset", {
      section_type: "all",
      sections_total: baseline.sections.length,
    });
    setSections(cloneJson(baseline.sections) || []);
    setBranding(cloneJson(baseline.branding) || {});
    setRemovedSections([]);
    clearAllApprovals();
    markDirty();
  }, [clearAllApprovals, markDirty, trackPersonalize]);

  const toggleHistory = useCallback(async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    setHistoryBusy(true);
    setHistoryError("");
    try {
      const response = await axios.get(`${API_URL}/computations/${uuid}/history/`, {
        headers: authHeader(),
      });
      setHistoryVersions(response.data.versions || []);
    } catch (err) {
      const axiosErr = /** @type {{response?: {data?: {error?: string}}}} */ (err);
      setHistoryError(axiosErr.response?.data?.error || "Couldn't load version history.");
    } finally {
      setHistoryBusy(false);
    }
  }, [historyOpen, uuid]);

  /** @param {number} version */
  const restoreHistoryVersion = useCallback(
    async (/** @type {number} */ version) => {
      if (!doc || dirty) {
        setHistoryError("Save or retry your current edits before restoring an earlier version.");
        return;
      }
      if (!window.confirm(`Restore version ${version}? Your current version stays in history.`)) {
        return;
      }
      setHistoryBusy(true);
      setHistoryError("");
      let observingJob = false;
      try {
        const response = await axios.post(
          `${API_URL}/computations/${uuid}/restore-version/`,
          { version, expected_version: doc.version },
          { headers: authHeader() }
        );
        const committedVersion = applyResponseVersion(response.data) || doc.version;
        const jobId = reportJobId(response.data);
        setActiveReportJobId(jobId);
        await fetchDoc();
        setHistoryOpen(false);
        if (jobId) {
          observingJob = true;
          setRestoreBaseVersion(committedVersion);
          setSaveStatus("rendering");
        } else {
          // Compatibility with API versions that predate durable job ids.
          setSaveStatus("ready");
        }
      } catch (err) {
        const axiosErr = /** @type {{response?: {status?: number, data?: {error?: string}}}} */ (
          err
        );
        const saved = savedWriteResponse(err);
        if (saved) {
          applyResponseVersion(saved);
          setActiveReportJobId(null);
          setSaveStatus("error");
          await fetchDoc();
          const message = jobFailureMessage(
            { message: axiosErr.response?.data?.error },
            "That version was restored, but publishing it could not start."
          );
          setErrorDetail(message);
          setHistoryError(message);
          console.error("Restore publish request failed:", err);
        } else {
          setHistoryError(
            axiosErr.response?.status === 409
              ? "This listing prep changed in another tab. Reload before restoring a version."
              : axiosErr.response?.data?.error || "Couldn't restore that version."
          );
        }
      } finally {
        if (!observingJob) setHistoryBusy(false);
      }
    },
    [doc, dirty, uuid, fetchDoc, applyResponseVersion]
  );

  // Restoring commits content synchronously, then publishes that version in a
  // durable rerender job. Observe it so a worker failure never spins for ten
  // minutes or silently leaves exports stale.
  usePollReportDocument({
    active: restoreBaseVersion != null,
    uuid,
    jobId: activeReportJobId,
    isReady: (data) =>
      (data.rendered_version || 0) >= (restoreBaseVersion || Number.MAX_SAFE_INTEGER),
    onReady: (data) => {
      // Every section's content just changed to the restored version — the
      // pre-restore "Looks good" approvals no longer describe it.
      adoptFreshDocument(data, { clearApprovals: true });
      setRestoreBaseVersion(null);
      setHistoryBusy(false);
    },
    onFailed: (failure) =>
      handleReportJobFailure(failure, "The restored version could not be published."),
    onTimeout: () => {
      setActiveReportJobId(null);
      setRestoreBaseVersion(null);
      setHistoryBusy(false);
      setSaveStatus("error");
      setErrorDetail(
        "Publishing the restored version is taking longer than expected. Refresh to check it, or try again."
      );
    },
  });

  const submitFeedback = useCallback(async () => {
    const message = feedbackMessage.trim();
    if (!doc || !message) {
      setFeedbackStatus("error");
      return;
    }
    setFeedbackStatus("sending");
    try {
      await axios.post(
        `${API_URL}/computations/${uuid}/feedback/`,
        {
          kind: feedbackKind,
          section_id: activeSectionRef.current || "",
          document_version: doc.version,
          message,
        },
        { headers: authHeader() }
      );
      setFeedbackMessage("");
      setFeedbackStatus("sent");
    } catch {
      setFeedbackStatus("error");
    }
  }, [doc, feedbackKind, feedbackMessage, uuid]);

  /** @param {string} field @param {unknown} value */
  const updateBranding = useCallback(
    (/** @type {string} */ field, /** @type {unknown} */ value) => {
      setBranding((prev) => ({ ...prev, [field]: value }));
      markDirty();
    },
    [markDirty]
  );

  /** @param {File | null | undefined} file */
  const handleLogoFile = useCallback(
    async (/** @type {File | null | undefined} */ file) => {
      if (!file) return;
      setLogoError(null);
      try {
        const dataUrl = await fileToImageDataUrl(file);
        setBranding((prev) => ({ ...prev, logo_url: dataUrl }));
        markDirty();
      } catch (err) {
        setLogoError(/** @type {Error} */ (err).message || "Couldn't use that image.");
      }
    },
    [markDirty]
  );

  const removeLogo = useCallback(() => {
    setLogoError(null);
    setBranding((prev) => ({ ...prev, logo_url: "" }));
    markDirty();
  }, [markDirty]);

  /** @param {File | null | undefined} file */
  const handleHeadshotFile = useCallback(
    async (/** @type {File | null | undefined} */ file) => {
      if (!file) return;
      setLogoError(null);
      try {
        const dataUrl = await fileToImageDataUrl(file);
        setBranding((prev) => ({ ...prev, headshot_url: dataUrl }));
        markDirty();
      } catch (err) {
        setLogoError(/** @type {Error} */ (err).message || "Couldn't use that image.");
      }
    },
    [markDirty]
  );

  const removeHeadshot = useCallback(() => {
    setLogoError(null);
    setBranding((prev) => ({ ...prev, headshot_url: "" }));
    markDirty();
  }, [markDirty]);

  // Centralised handling for a failed write (save / revise / regenerate). A 401
  // means the session expired; a 403 means the viewer doesn't own this listing prep —
  // in both cases we drop to read-only with a specific, friendly message instead
  // of a silent generic error and an editor that looks editable but isn't.
  /**
   * @param {unknown} err
   * @param {string} logLabel
   * @returns {boolean} whether the API confirmed local content was committed
   */
  const handleWriteError = useCallback(
    (/** @type {unknown} */ err, /** @type {string} */ logLabel) => {
      const axiosErr = /** @type {{response?: {status?: number}}} */ (err);
      const sc = axiosErr.response?.status;
      const saved = savedWriteResponse(err);
      setRegenerating(false);
      setRevisingSectionId(null);
      setUpdatingComps(false);
      setUpdatingPhotosSectionId(null);
      setActiveReportJobId(null);
      setReviseBaseVersion(null);
      setCompsBaseVersion(null);
      setPhotosBaseVersion(null);
      setRegenBaseVersion(null);
      // A short balance is a 409 too — but it is the paywall's business, not
      // a version conflict. Branch on the fixed contract FIRST; nothing was
      // written, so saving stays enabled and the listing is untouched.
      const insufficient = insufficientCreditsFrom(err);
      if (insufficient) {
        setCreditsBlock(insufficient);
        setErrorDetail(null);
        setSaveStatus("ready");
        return false;
      }
      if (saved) {
        applyResponseVersion(saved);
        const apiMessage = typeof saved.error === "string" ? saved.error : "";
        setErrorDetail(
          jobFailureMessage(
            { message: apiMessage },
            "Your edits were saved, but the listing-prep update could not start."
          )
        );
        console.error(logLabel, err);
        setSaveStatus("error");
        return true;
      }
      if (sc === 401) {
        clearToken();
        setEditable(false);
        setErrorDetail("Your session expired — please sign in again to keep editing.");
        setSaveStatus("error");
        return false;
      }
      if (sc === 403) {
        setEditable(false);
        setErrorDetail(
          "You can only edit listing preps you own. Sign in as the owner to make changes."
        );
        setSaveStatus("error");
        return false;
      }
      if (sc === 409) {
        setErrorDetail(
          "This listing prep changed in another tab. Your edits are still here; reload to compare, then apply them again."
        );
        setSaveStatus("error");
        return false;
      }
      setErrorDetail(null);
      console.error(logLabel, err);
      setSaveStatus("error");
      return false;
    },
    [applyResponseVersion]
  );

  /**
   * @param {{message?: unknown} | null} failure
   * @param {string} fallback
   * @param {string} [suffix] Appended after the actionable message (the refund fact).
   */
  const handleReportJobFailure = useCallback(
    (
      /** @type {{message?: unknown} | null} */ failure,
      /** @type {string} */ fallback,
      /** @type {string} */ suffix = ""
    ) => {
      setActiveReportJobId(null);
      setRegenerating(false);
      setRevisingSectionId(null);
      setUpdatingComps(false);
      setUpdatingPhotosSectionId(null);
      setReviseBaseVersion(null);
      setCompsBaseVersion(null);
      setPhotosBaseVersion(null);
      setRegenBaseVersion(null);
      setRestoreBaseVersion(null);
      setHistoryBusy(false);
      // NOT setSaveStatus("error"): that gate pauses auto-save, and a failed
      // AI job says nothing about whether PATCH saves work. Pausing here meant
      // an agent could keep editing after a failed rewrite with nothing
      // persisting until they noticed the small retry affordance.
      setSaveStatus("ready");
      setErrorDetail(`${jobFailureMessage(failure, fallback)}${suffix}`);
    },
    []
  );

  // --- Focus-sync helpers (editor rail ⇄ live preview) ---
  /** @param {string | null} sectionId */
  const flashPreviewSection = useCallback((/** @type {string} */ sectionId) => {
    setPreviewHighlightId(sectionId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setPreviewHighlightId(null), 1800);
  }, []);
  useEffect(
    () => () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    },
    []
  );

  // Scroll the preview to a section + flash it. Desktop scrolls the preview
  // rail's own scroll container (never the page, so the editor rail stays
  // put); mobile scrolls the page, but only when the preview is actually
  // visible (Preview tab open).
  /** @param {string} sectionId @param {{force?: boolean, flash?: boolean}} [options] */
  const syncPreviewToSection = useCallback(
    (/** @type {string} */ sectionId, { force = false, flash = true } = {}) => {
      if (!sectionId) return;
      if (!force && lastSyncedRef.current === sectionId) return;
      lastSyncedRef.current = sectionId;
      const el = document.getElementById(`preview-${sectionId}`);
      if (!el) return;
      const rail = previewRailRef.current;
      if (isDesktopViewport() && rail && typeof rail.scrollTo === "function") {
        const railRect = rail.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        // Our own scroll — keep the preview's spy quiet while it plays out.
        previewSpySuppressUntilRef.current = Date.now() + 1200;
        rail.scrollTo({
          top: rail.scrollTop + (elRect.top - railRect.top) - 16,
          behavior: "smooth",
        });
      } else if (el.offsetParent !== null && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      // The flash marks deliberate actions (edit, rewrite, AI updates); the
      // scroll-walking spy syncs silently or it would blink on every scroll.
      if (flash) flashPreviewSection(sectionId);
    },
    [flashPreviewSection]
  );

  // ---- Shared tails for the five AI/publish poll handlers ----------------
  // Every onReady ends by adopting the fresh document the same way, and every
  // onTimeout fails the same way. These were five hand-copied blocks — a
  // missed setter in one copy is exactly how the restore-poll's stale-
  // approvals bug happened.
  const adoptFreshDocument = useCallback(
    (
      /** @type {any} */ data,
      /** @type {{flash?: string, syncSectionId?: string | null, clearApprovals?: boolean, scrollTop?: boolean}} */ {
        flash,
        syncSectionId,
        clearApprovals = false,
        scrollTop = false,
      } = {}
    ) => {
      setDoc(data);
      documentVersionRef.current = reportVersion(data.version) || documentVersionRef.current;
      setSections(data.content?.sections || []);
      resetPhotoBaselines(data.content?.sections || []);
      setBranding(data.branding || {});
      if (clearApprovals) clearAllApprovals();
      setActiveReportJobId(null);
      if (syncSectionId) syncPreviewToSection(syncSectionId, { force: true });
      if (flash) flashAiDone(flash);
      if (scrollTop) window.scrollTo({ top: 0, behavior: "smooth" });
      dirtyRef.current = false;
      setDirty(false);
      clearReportDraft(uuid, draftBackupRef.current?.id);
      draftBackupRef.current = null;
      setDraftBackupFailed(false);
      setSaveStatus("ready");
    },
    [uuid, resetPhotoBaselines, clearAllApprovals, syncPreviewToSection, flashAiDone]
  );

  const aiPollTimedOut = useCallback((/** @type {string} */ message) => {
    setActiveReportJobId(null);
    setSaveStatus("error");
    setErrorDetail(message);
  }, []);

  // A full re-prepare may spend credits or consume an allowance. Its saved
  // request survives a lost response and is not replaced by a fresh purchase.
  const regenPurchaseScope = useMemo(
    () => ({ action: /** @type {const} */ ("regenerate"), targetUuid: uuid }),
    [uuid]
  );
  const regenPurchaseBridge = useRef(/** @type {any} */ (null));
  const regenPollWindowRef = useRef({ id: "", until: 0 });
  const [regenPurchaseMessage, setRegenPurchaseMessage] = useState("");
  const regenPurchase = usePaidAttempt({
    scope: regenPurchaseScope,
    enabled:
      status === "ok" &&
      editable &&
      !!reprepareQuote &&
      loadedScopeRef.current === editorScope &&
      loadedTokenRef.current === getToken(),
    onAccepted: async (receipt, record) => {
      const token = getToken();
      const current = () => editorScopeRef.current === editorScope && getToken() === token;
      const job = receipt.job;
      if (
        !receipt.replacement_chain_complete ||
        receipt.replacement_jobs.length > 0 ||
        !job ||
        job.job_id !== receipt.attempt.job_id ||
        job.mode !== "regenerate"
      ) {
        setRegenerating(false);
        setRegenBaseVersion(null);
        setRegenPurchaseMessage(
          "Your re-prepare request was accepted, but its job status is unavailable. Check status before starting another."
        );
        return;
      }
      const settlement = receipt.settlement;
      const money = `${settlement.charged_credits} credits charged; ${settlement.refunded_credits} credits refunded for this request.`;
      const allowance =
        settlement.free_redos_used > 0
          ? settlement.free_redos_restored === null
            ? " Free re-run restoration has not been confirmed."
            : ` ${settlement.free_redos_restored} free re-run${settlement.free_redos_restored === 1 ? "" : "s"} restored for this request.`
          : "";
      const acknowledge = () => {
        const cleared = regenPurchaseBridge.current?.acknowledge(record.attempt_id);
        if (!cleared)
          setRegenPurchaseMessage(
            "The outcome is confirmed, but your browser could not clear its saved recovery record. Check status again before another re-prepare."
          );
        return cleared;
      };
      if (job.status === "failed") {
        handleReportJobFailure(null, `Re-preparation did not finish. ${money}${allowance}`);
        setRegenPurchaseMessage(`Re-preparation did not finish. ${money}${allowance}`);
        acknowledge();
        void accountRefresh();
        return;
      }
      if (job.status === "completed") {
        setRegenerating(false);
        setRegenBaseVersion(null);
        setActiveReportJobId(null);
        try {
          const fresh = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
            headers: authHeader(),
            timeout: 10000,
          });
          if (!current()) return;
          if (
            dirtyRef.current ||
            recoveredDraft ||
            fresh.data?.editable !== true ||
            !((reportVersion(fresh.data?.version) || 0) > record.request.base_version)
          ) {
            setRegenPurchaseMessage(
              "Re-preparation finished. Your local edits are still here; reload to compare the saved result before another re-prepare."
            );
            return;
          }
          setRevisedBefore({});
          adoptFreshDocument(fresh.data, {
            clearApprovals: true,
            flash: "Fresh draft ready — your edits were woven in",
            scrollTop: true,
          });
          setErrorDetail(null);
          setRegenPurchaseMessage(`Re-preparation finished. ${money}`);
          acknowledge();
          void accountRefresh();
        } catch {
          if (current())
            setRegenPurchaseMessage(
              "Re-preparation finished, but the saved report could not be loaded. Check status to try that read again."
            );
        }
        return;
      }
      setErrorDetail(null);
      setRegenerating(true);
      setRegenBaseVersion(record.request.base_version);
      setActiveReportJobId(job.job_id);
      setRegenPurchaseMessage(
        "Your original re-prepare request is running. Its saved attempt will be checked; no new purchase is needed."
      );
      if (regenPollWindowRef.current.id !== record.attempt_id) {
        regenPollWindowRef.current = { id: record.attempt_id, until: Date.now() + 10 * 60 * 1000 };
      }
    },
    onRejected: async (receipt, record) => {
      const response = receipt.attempt.response;
      if (!regenPurchaseBridge.current?.acknowledge(record.attempt_id)) {
        setRegenPurchaseMessage(
          "The request was not accepted, but your browser could not clear its saved recovery record. Check status again before retrying."
        );
        return;
      }
      setRegenPurchaseMessage(
        "This re-prepare request was not accepted. No credits were charged for this request."
      );
      handleWriteError(
        { response: { status: response.status_code, data: response.data } },
        "Re-prepare request rejected:"
      );
    },
  });
  regenPurchaseBridge.current = regenPurchase;
  const checkRegenPurchase = regenPurchase.check;
  const submitRegenPurchase = regenPurchase.submit;

  useEffect(() => {
    if (
      !regenPurchase.pending ||
      regenPurchase.phase !== "accepted" ||
      !["queued", "processing"].includes(regenPurchase.receipt?.job?.status)
    )
      return undefined;
    if (Date.now() >= regenPollWindowRef.current.until) {
      setRegenerating(false);
      setRegenPurchaseMessage(
        "Re-preparation is taking longer than expected. Its attempt is saved; check status before trying another purchase."
      );
      return undefined;
    }
    const timer = setTimeout(() => {
      void checkRegenPurchase();
    }, 3000);
    return () => clearTimeout(timer);
  }, [regenPurchase.pending, regenPurchase.phase, regenPurchase.receipt, checkRegenPurchase]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: A route change resets only transient recovery UI, never the saved attempt.
  useEffect(() => {
    setRegenerating(false);
    setRegenBaseVersion(null);
    setRegenPurchaseMessage("");
    regenPollWindowRef.current = { id: "", until: 0 };
  }, [uuid]);

  // Scroll-walking (desktop): as the left rail scrolls through sections, the
  // preview follows section-to-section. Deliberately NOT a proportional
  // scroll-lock — pane heights don't correspond. The "current" section is the
  // last card whose top has crossed a line ~30% down the viewport; syncs are
  // debounced and skipped when the section hasn't changed, and the preview's
  // own scrollbar stays free (one-directional, so no feedback loops).
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  useEffect(() => {
    if (status !== "ok") return undefined;
    let pending = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
    const onScroll = () => {
      if (!isDesktopViewport()) return;
      if (Date.now() < spySuppressUntilRef.current) return;
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        if (Date.now() < spySuppressUntilRef.current) return;
        // The next section takes over only once its title nearly reaches the
        // top (15% line) — anything higher switched while still mid-read.
        const line = window.innerHeight * 0.15;
        let currentId = null;
        for (const s of sectionsRef.current) {
          const el = document.getElementById(`sec-${s.section_id}`);
          if (!el) continue;
          if (el.getBoundingClientRect().top <= line) currentId = s.section_id;
          else break;
        }
        if (currentId) {
          activeSectionRef.current = currentId;
          setActiveSectionId(currentId);
          syncPreviewToSection(currentId, { flash: false });
        }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (pending) clearTimeout(pending);
    };
  }, [status, syncPreviewToSection]);

  // "I'm working on this section" — expanding/editing a card follows it in the
  // preview (desktop live; mobile just remembers it for the next tab switch).
  /** @param {string} sectionId */
  const activateSection = useCallback(
    (/** @type {string | null | undefined} */ sectionId) => {
      if (!sectionId) return;
      activeSectionRef.current = sectionId;
      setActiveSectionId(sectionId);
      if (isDesktopViewport()) syncPreviewToSection(sectionId);
    },
    [syncPreviewToSection]
  );

  // Mobile: opening the Preview tab jumps to the section that was being edited.
  const openPreviewView = useCallback(() => {
    setMobileView("preview");
    const target = activeSectionRef.current;
    if (target) setTimeout(() => syncPreviewToSection(target, { force: true }), 60);
  }, [syncPreviewToSection]);

  // Keep a tab-local recovery copy without ever overwriting a pending recovery choice.
  // The imperative dirty flag prevents a just-acknowledged save from recreating its backup.
  useEffect(() => {
    if (
      !dirty ||
      !dirtyRef.current ||
      !editable ||
      !doc ||
      recoveredDraft ||
      loadedScopeRef.current !== editorScope
    )
      return;
    const backup = saveReportDraft(uuid, {
      baseVersion: documentVersionRef.current,
      content: { ...doc.content, sections },
      branding,
    });
    if (backup) draftBackupRef.current = backup;
    setDraftBackupFailed(!backup);
  }, [dirty, editable, doc, sections, branding, recoveredDraft, uuid, editorScope]);

  const restoreRecoveredDraft = useCallback(() => {
    if (!recoveredDraft || !editable || recoveredDraft.baseVersion !== documentVersionRef.current)
      return;
    setSections(cloneJson(recoveredDraft.content.sections));
    setBranding(cloneJson(recoveredDraft.branding));
    draftBackupRef.current = recoveredDraft;
    setRecoveredDraft(null);
    clearAllApprovals();
    setMobileView("edit");
    markDirty();
  }, [recoveredDraft, editable, clearAllApprovals, markDirty]);

  const discardRecoveredDraft = useCallback(() => {
    if (!recoveredDraft) return;
    const discarded = clearReportDraft(uuid, recoveredDraft.id);
    // If another save replaced this record, keep that newer recovery choice.
    const remaining = discarded ? null : loadReportDraft(uuid);
    setRecoveredDraft(remaining);
    setDraftBackupFailed(!discarded && remaining?.id === recoveredDraft.id);
  }, [uuid, recoveredDraft]);

  const downloadRecoveredDraft = useCallback(() => {
    if (!recoveredDraft) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(recoveredDraft, null, 2)], {
        type: "application/json",
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `report-${uuid}-unsaved-draft.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the browser time to start the download before releasing the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [uuid, recoveredDraft]);

  // One flight drains the newest revision, with each PATCH based on the last
  // acknowledged version. Neither a debounce nor Retry/Share can overlap it.
  // Response metadata updates the saved document, never newer local input.
  const autoSave = useCallback(() => {
    const existing = saveFlightRef.current;
    if (existing?.scope === editorScope) return existing.promise;
    if (
      editorScopeRef.current !== editorScope ||
      loadedScopeRef.current !== editorScope ||
      !latestDraftRef.current.doc ||
      !latestDraftRef.current.editable ||
      recoveredDraft
    )
      return Promise.resolve(false);
    if (!dirtyRef.current) return Promise.resolve(true);

    const flight = { scope: editorScope, promise: Promise.resolve(false) };
    saveFlightRef.current = flight;
    setErrorDetail(null);
    setSaveStatus("saving");
    flight.promise = (async () => {
      try {
        while (dirtyRef.current && editorScopeRef.current === editorScope) {
          const snapshot = latestDraftRef.current;
          const saveRevision = dirtyRevisionRef.current;
          const content = { ...snapshot.doc?.content, sections: snapshot.sections };
          const expectedVersion = documentVersionRef.current;
          const resp = await axios.patch(
            `${API_URL}/computations/${uuid}/report-document/`,
            {
              content,
              branding: snapshot.branding,
              render: false,
              expected_version: expectedVersion,
            },
            { headers: authHeader(), timeout: 30000 }
          );
          if (editorScopeRef.current !== editorScope) return false;
          const acknowledgedVersion = reportVersion(resp.data?.version);
          if (acknowledgedVersion == null || acknowledgedVersion < expectedVersion) {
            throw new Error("The server did not confirm the saved report version.");
          }
          applyResponseVersion(resp.data, { content, branding: snapshot.branding });
          if (dirtyRevisionRef.current === saveRevision) {
            // Clear synchronously before Share navigates; Prompt must not see
            // the previous render's dirty state after a successful flush.
            dirtyRef.current = false;
            clearReportDraft(uuid, draftBackupRef.current?.id);
            draftBackupRef.current = null;
            setDraftBackupFailed(false);
            setDirty(false);
            setSaveStatus("saved");
            return true;
          }
          // A newer keystroke is still local. Preserve it at the updated base
          // before the next request, including across refresh during that request.
          const latest = latestDraftRef.current;
          const backup = saveReportDraft(uuid, {
            baseVersion: documentVersionRef.current,
            content: { ...latest.doc?.content, sections: latest.sections },
            branding: latest.branding,
          });
          if (backup) draftBackupRef.current = backup;
          setDraftBackupFailed(!backup);
        }
        return editorScopeRef.current === editorScope;
      } catch (err) {
        if (editorScopeRef.current !== editorScope) return false;
        const statusCode =
          /** @type {{response?: {status?: number}}} */ (err).response?.status || 0;
        trackPersonalize(
          statusCode === 409 ? "personalize_save_conflict" : "personalize_save_failed",
          { status: statusCode }
        );
        handleWriteError(err, "Auto-save failed:");
        return false;
      } finally {
        if (saveFlightRef.current === flight) saveFlightRef.current = null;
      }
    })();
    return flight.promise;
  }, [uuid, editorScope, recoveredDraft, handleWriteError, trackPersonalize, applyResponseVersion]);

  // Debounced auto-save: fire after an idle gap, never mid-AI-op.
  // biome-ignore lint/correctness/useExhaustiveDependencies: each text/branding change restarts the idle timer; autoSave reads the newest snapshot from a ref.
  useEffect(() => {
    if (
      !dirty ||
      !editable ||
      revisingSectionId ||
      saveStatus === "error" ||
      saveStatus === "saving" ||
      recoveredDraft ||
      regenerating ||
      updatingComps ||
      updatingPhotosSectionId
    ) {
      return undefined;
    }
    const handle = setTimeout(() => autoSave(), AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [
    dirty,
    editable,
    sections,
    branding,
    recoveredDraft,
    saveStatus,
    revisingSectionId,
    regenerating,
    updatingComps,
    updatingPhotosSectionId,
    autoSave,
  ]);

  // Flush a pending edit before leaving for Share, so a last-second tweak made
  // inside the debounce window isn't lost from the shared package.
  const continueToShare = useCallback(async () => {
    if (shareFlightRef.current === editorScope || recoveredDraft) return;
    shareFlightRef.current = editorScope;
    try {
      if (dirtyRef.current || saveFlightRef.current?.scope === editorScope) {
        setContinuingToShare(true);
        const saved = await autoSave();
        if (!saved || editorScopeRef.current !== editorScope) return;
      }
      // Read the final drained snapshot, not the version captured on click.
      const latestSections = latestDraftRef.current.sections;
      const total = latestSections.length;
      const edited = editedSectionsRef.current.size;
      trackPersonalize("personalize_completed", {
        seconds_active: openedAtRef.current
          ? Math.round((Date.now() - openedAtRef.current) / 1000)
          : 0,
        sections_total: total,
        sections_approved: latestSections.reduce(
          (n, s) => n + (approvals[s.section_id] ? 1 : 0),
          0
        ),
        sections_edited: edited,
        pct_edited: total ? Math.round((edited / total) * 100) : 0,
      });
      history.push(`/reports/${uuid}/share`);
    } finally {
      if (shareFlightRef.current === editorScope) shareFlightRef.current = null;
      if (editorScopeRef.current === editorScope) setContinuingToShare(false);
    }
  }, [autoSave, editorScope, recoveredDraft, history, uuid, approvals, trackPersonalize]);

  const confirmNavigation = useCallback(
    (/** @type {{pathname: string}} */ nextLocation) =>
      !dirtyRef.current || nextLocation.pathname === location.pathname
        ? true
        : "Your latest edits have not been saved. Leave this page?",
    [location.pathname]
  );

  // Warn on tab-close / hard nav-away while an edit is still pending.
  useEffect(() => {
    if (!dirty) return undefined;
    /** @param {BeforeUnloadEvent} e */
    const warn = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /** @param {number} index @param {string} instruction */
  const handleRevise = useCallback(
    async (/** @type {number} */ index, /** @type {string} */ instruction) => {
      if (!doc) return;
      const section = sections[index];
      if (!section?.section_id) return;
      setErrorDetail(null);
      // Snapshot the section BEFORE the rewrite so we can show what changed.
      pendingReviseRef.current = { id: section.section_id, before: section };
      editedSectionsRef.current.add(section.section_id);
      trackPersonalize("personalize_section_rewritten", {
        section_type: section.__type__ || "unknown",
        preset: REWRITE_CHIPS.includes(instruction) ? instruction : "custom",
      });
      setRevisingSectionId(section.section_id);
      // Send the current (possibly hand-edited) content so the revision builds
      // on it; the server saves it, then the worker revises + re-renders.
      const content = { ...doc.content, sections };
      try {
        const resp = await axios.post(
          `${API_URL}/computations/${uuid}/revise/`,
          {
            section_id: section.section_id,
            instruction,
            content,
            branding,
            base_version: doc.version,
          },
          { headers: authHeader() }
        );
        const baseVersion = applyResponseVersion(resp.data, { content, branding }) || doc.version;
        setActiveReportJobId(reportJobId(resp.data));
        setReviseBaseVersion(baseVersion);
      } catch (err) {
        handleWriteError(err, "AI revise request failed:");
      }
    },
    [doc, sections, branding, uuid, handleWriteError, trackPersonalize, applyResponseVersion]
  );

  // Undo a recent AI revise for a section by restoring the captured 'before'
  // snapshot in `revisedBefore`. This is a local, immediate undo that marks
  // the doc dirty and persists via the normal autosave path.
  const undoReviseForSection = useCallback(
    (/** @type {string} */ sectionId) => {
      const before = revisedBefore[sectionId];
      if (!before) return;
      const idx = sections.findIndex((s) => s.section_id === sectionId);
      if (idx === -1) return;
      replaceSection(idx, cloneJson(before));
      setRevisedBefore((prev) => {
        const next = { ...prev };
        delete next[sectionId];
        return next;
      });
      markDirty();
      trackPersonalize("personalize_section_undo_rewrite", {
        section_type: before.__type__ || "unknown",
      });
    },
    [revisedBefore, sections, replaceSection, markDirty, trackPersonalize]
  );

  // Poll for the AI revision: the worker bumps the version once it has revised +
  // re-rendered, at which point we reload the (revised) content.
  usePollReportDocument({
    active: !!revisingSectionId && reviseBaseVersion != null,
    uuid,
    jobId: activeReportJobId,
    isReady: (data) => (data.version || 0) > (reviseBaseVersion ?? 0),
    onReady: (data) => {
      if (revisingSectionId == null) return;
      // The rewritten section is new content — clear its "Looks good".
      unapproveSection(revisingSectionId);
      // Stash the before/after so the section can show what the AI changed.
      if (pendingReviseRef.current?.id === revisingSectionId) {
        const snap = pendingReviseRef.current;
        setRevisedBefore((prev) => ({ ...prev, [snap.id]: snap.before }));
        pendingReviseRef.current = null;
      }
      // Show the rewrite where it landed: sync + flash it in the preview.
      adoptFreshDocument(data, {
        syncSectionId: revisingSectionId,
        flash: "Rewrite ready — the section below is updated",
      });
      setRevisingSectionId(null);
      setReviseBaseVersion(null);
    },
    onFailed: (failure) =>
      handleReportJobFailure(failure, "The AI rewrite failed before it could update this section."),
    onTimeout: () => {
      aiPollTimedOut(
        "The AI rewrite is taking longer than expected. Refresh in a moment, or try again."
      );
      setRevisingSectionId(null);
      setReviseBaseVersion(null);
    },
  });

  // The agent curated the comp addresses — queue the comps-only AI update.
  // Sends the current (possibly hand-edited) content so the update builds on
  // it, exactly like revise.
  /** @param {number} index @param {string[]} addresses @param {string[]} excludedAddresses */
  const handleUpdateComps = useCallback(
    async (
      /** @type {number} */ index,
      /** @type {string[]} */ addresses,
      /** @type {string[]} */ excludedAddresses
    ) => {
      if (!doc) return;
      const section = sections[index];
      if (!section?.section_id) return;
      setErrorDetail(null);
      setCompsWarning(null);
      requestedCompsRef.current = addresses;
      markSectionTouched(section.section_id, section.__type__);
      trackPersonalize("personalize_comps_updated", {
        comps_total: addresses.length,
        comps_removed: excludedAddresses.length,
      });
      setUpdatingComps(true);
      const content = { ...doc.content, sections };
      try {
        const resp = await axios.post(
          `${API_URL}/computations/${uuid}/revise-comps/`,
          {
            addresses,
            excluded_addresses: excludedAddresses,
            content,
            branding,
            base_version: doc.version,
          },
          { headers: authHeader() }
        );
        const baseVersion = applyResponseVersion(resp.data, { content, branding }) || doc.version;
        setActiveReportJobId(reportJobId(resp.data));
        setCompsBaseVersion(baseVersion);
      } catch (err) {
        handleWriteError(err, "Comps update request failed:");
      }
    },
    [
      doc,
      sections,
      branding,
      uuid,
      markSectionTouched,
      handleWriteError,
      trackPersonalize,
      applyResponseVersion,
    ]
  );

  // Poll for the comps update; on completion, verify every requested address
  // actually made it into the rebuilt comp set and warn about the ones the
  // AI's search couldn't find.
  usePollReportDocument({
    active: updatingComps && compsBaseVersion != null,
    uuid,
    jobId: activeReportJobId,
    isReady: (data) => (data.version || 0) > (compsBaseVersion ?? 0),
    onReady: (data) => {
      const newSections = /** @type {Section[]} */ (data.content?.sections || []);
      const comp = newSections.find((s) => s.__type__ === "ComparableAnalysisSection");
      const missing = unverifiedCompAddresses(
        requestedCompsRef.current,
        comp ? compAddresses(comp) : []
      );
      setCompsWarning(
        missing.length
          ? `We couldn't find listing data for: ${missing.join("; ")}. Double-check the address — picking it from the suggestions helps — and try again.`
          : null
      );
      // Everything the update touched is new content — the rebuilt comps AND
      // any sections the worker's consistency pass brought along (pricing
      // citations in the summary/conclusion). Clear each one's "Looks good".
      for (const id of changedSectionIds(sections, newSections)) unapproveSection(id);
      if (comp) unapproveSection(comp.section_id);
      // Show the rebuilt comps where they landed: sync + flash in the preview.
      adoptFreshDocument(data, {
        syncSectionId: comp?.section_id,
        flash: "Comparables updated — pricing rebuilt around your picks",
      });
      setUpdatingComps(false);
      setCompsBaseVersion(null);
    },
    onFailed: (failure) =>
      handleReportJobFailure(failure, "The comparables update failed before it finished."),
    onTimeout: () => {
      aiPollTimedOut(
        "Updating the comparables is taking longer than expected. Refresh in a moment, or try again."
      );
      setUpdatingComps(false);
      setCompsBaseVersion(null);
    },
  });

  // The agent curated a photo section's set/order — queue the photos-only AI
  // rebuild of that section's write-up. Sends the current (possibly
  // hand-edited) content so the update builds on it, exactly like revise.
  /** @param {number} index @param {string[]} photos */
  const handleUpdatePhotos = useCallback(
    async (/** @type {number} */ index, /** @type {string[]} */ photos) => {
      if (!doc) return;
      const section = sections[index];
      if (!section?.section_id) return;
      setErrorDetail(null);
      markSectionTouched(section.section_id, section.__type__);
      trackPersonalize("personalize_photos_updated", {
        section_type: section.__type__ || "unknown",
        photo_count: photos.length,
      });
      setUpdatingPhotosSectionId(section.section_id);
      const content = { ...doc.content, sections };
      try {
        const resp = await axios.post(
          `${API_URL}/computations/${uuid}/revise-photos/`,
          {
            section_id: section.section_id,
            photos,
            content,
            branding,
            base_version: doc.version,
          },
          { headers: authHeader() }
        );
        const baseVersion = applyResponseVersion(resp.data, { content, branding }) || doc.version;
        setActiveReportJobId(reportJobId(resp.data));
        setPhotosBaseVersion(baseVersion);
      } catch (err) {
        setUpdatingPhotosSectionId(null);
        handleWriteError(err, "Photos update request failed:");
      }
    },
    [
      doc,
      sections,
      branding,
      uuid,
      markSectionTouched,
      handleWriteError,
      trackPersonalize,
      applyResponseVersion,
    ]
  );

  // Poll for the photos rebuild; on completion reload and show where it landed.
  usePollReportDocument({
    active: updatingPhotosSectionId != null && photosBaseVersion != null,
    uuid,
    jobId: activeReportJobId,
    isReady: (data) => (data.version || 0) > (photosBaseVersion ?? 0),
    onReady: (data) => {
      const rebuiltId = updatingPhotosSectionId;
      if (rebuiltId == null) return;
      const newSections = /** @type {Section[]} */ (data.content?.sections || []);
      // The rebuilt section AND anything the consistency pass touched (the
      // conclusion citing this room's recommendations) are new content.
      for (const id of changedSectionIds(sections, newSections)) unapproveSection(id);
      unapproveSection(rebuiltId);
      adoptFreshDocument(data, {
        syncSectionId: rebuiltId,
        flash: "Write-up updated around your photos",
      });
      setUpdatingPhotosSectionId(null);
      setPhotosBaseVersion(null);
    },
    onFailed: (failure) =>
      handleReportJobFailure(failure, "The photo write-up update failed before it finished."),
    onTimeout: () => {
      aiPollTimedOut(
        "Updating the write-up is taking longer than expected. Refresh in a moment, or try again."
      );
      setUpdatingPhotosSectionId(null);
      setPhotosBaseVersion(null);
    },
  });

  const startRegenerate = useCallback(async () => {
    const guidance = regenGuidance.trim();
    if (!doc) return;
    if (reprepareQuote) {
      setErrorDetail(null);
      setRegenPurchaseMessage("");
      const recovering = !!regenPurchase.pending;
      const receipt = await submitRegenPurchase({
        base_version: doc.version,
        instruction: guidance,
      });
      if (
        !recovering &&
        receipt?.attempt.status === "accepted" &&
        receipt.settlement.charged_credits > 0
      ) {
        track("credits_spent", {
          action: "analysis",
          cost: receipt.settlement.charged_credits,
          attempt_id: receipt.attempt.attempt_id,
        });
      }
      return;
    }
    setErrorDetail(null);
    setRegenerating(true);
    try {
      const resp = await axios.post(
        `${API_URL}/computations/${uuid}/regenerate/`,
        { ...(guidance ? { instruction: guidance } : {}), base_version: doc.version },
        { headers: authHeader() }
      );
      setRegenBaseVersion(applyResponseVersion(resp.data) || doc.version);
      setActiveReportJobId(reportJobId(resp.data));
    } catch (err) {
      handleWriteError(err, "Regenerate request failed:");
    }
  }, [
    uuid,
    doc,
    regenGuidance,
    handleWriteError,
    applyResponseVersion,
    reprepareQuote,
    submitRegenPurchase,
    regenPurchase.pending,
  ]);

  const handleRegenerate = useCallback(() => {
    if (!doc) return;
    if (reprepareQuote) {
      // Pricing v2: the spend is confirmed as one fact in an in-app sheet;
      // the balance is re-read when it opens (never hard-blocked on).
      void accountRefresh();
      setReprepareConfirm(true);
      return;
    }
    const guidance = regenGuidance.trim();
    const confirmMsg = guidance
      ? `Re-prepare this listing with your guidance ("${guidance}")? AI writes a fresh draft and weaves your hand edits into it. Takes a few minutes.`
      : "Re-prepare this listing from scratch? AI writes a fresh draft and weaves your hand edits into it. Takes a few minutes.";
    if (!window.confirm(confirmMsg)) return;
    void startRegenerate();
  }, [doc, regenGuidance, reprepareQuote, accountRefresh, startRegenerate]);

  // Poll for the full regeneration (a fresh LLM pass — minutes, not seconds).
  usePollReportDocument({
    active: regenerating && regenBaseVersion != null && !regenPurchase.pending,
    uuid,
    jobId: activeReportJobId,
    isReady: (data) => (data.version || 0) > (regenBaseVersion ?? 0),
    onReady: (data) => {
      // A fresh whole-doc draft — every section is new, so reset approvals
      // and clear any stale per-section rewrite diffs.
      setRevisedBefore({});
      adoptFreshDocument(data, {
        clearApprovals: true,
        flash: "Fresh draft ready — your edits were woven in",
        scrollTop: true,
      });
      setRegenerating(false);
      setRegenBaseVersion(null);
    },
    onFailed: (failure) => {
      handleReportJobFailure(failure, "Re-preparing this listing prep failed before it finished.");
      if (reprepareQuote) void accountRefresh();
    },
    onTimeout: () => {
      aiPollTimedOut(
        "Re-preparing is taking longer than expected. Refresh in a moment to check on it."
      );
      setRegenerating(false);
      setRegenBaseVersion(null);
    },
  });

  const saveMessage = useMemo(() => {
    // A specific error (session expired / not owner) wins over everything.
    if (errorDetail) return errorDetail;
    if (regenPurchase.pending)
      return "Checking the saved re-prepare attempt — another purchase is paused.";
    if (!editable) return "Read-only — sign in as the owner to personalize.";
    if (restoreBaseVersion != null) return "Restoring and publishing this saved version…";
    // AI status lives in the activity pill (one voice, with a spinner) — the
    // save bar just steps aside while a job runs.
    if (regenerating || revisingSectionId || updatingComps || updatingPhotosSectionId) {
      return "AI is working — your edits are safe.";
    }
    if (dirty && saveStatus !== "saving" && saveStatus !== "error") {
      return "Unsaved changes — saving shortly…";
    }
    switch (saveStatus) {
      case "saving":
        return "Saving…";
      case "saved":
        return "Saved · your listing package updates when you Share";
      case "ready":
        return "Updated.";
      case "error":
        return "Something went wrong. Please try again.";
      default:
        return "Everything's drafted — review and add your touch.";
    }
  }, [
    dirty,
    saveStatus,
    revisingSectionId,
    editable,
    regenerating,
    regenPurchase.pending,
    updatingComps,
    updatingPhotosSectionId,
    errorDetail,
    restoreBaseVersion,
  ]);

  // Review progress: how many sections the agent has blessed, and what's next.
  const totalSections = sections.length;
  const approvedCount = sections.reduce((n, s) => n + (approvals[s.section_id] ? 1 : 0), 0);
  const nextSectionByOrder = useMemo(() => {
    // Before any scroll the spy hasn't fired, but the reader is looking at
    // the FIRST section — so "Next" is the second one, not a fallback pick.
    const currentIndex = activeSectionId
      ? sections.findIndex((s) => s.section_id === activeSectionId)
      : 0;
    if (currentIndex >= 0 && currentIndex < sections.length - 1) {
      return sections[currentIndex + 1];
    }
    return null;
  }, [sections, activeSectionId]);

  // Strictly document order, tracking the scroll position (Trello #17). The
  // old fallback jumped to the first UNAPPROVED section by priority — residue
  // of the removed forced-approval flow — which made the link read "Next:
  // Executive Summary" while you were looking at Executive Summary, and leap
  // backwards from the last section. On the last section there is no next.
  const nextSection = nextSectionByOrder;

  const firstPhotoSectionId = useMemo(
    () => sections.find((s) => PHOTO_SECTION_TYPES.has(s.__type__))?.section_id || null,
    [sections]
  );

  useEffect(() => {
    if (!activeSectionId && sections.length > 0) {
      setActiveSectionId(sections[0].section_id);
    }
  }, [sections, activeSectionId]);
  const deepLinkSectionId = openParam === "photos" ? firstPhotoSectionId : null;
  const compsSectionId =
    sections.find((s) => s.__type__ === "ComparableAnalysisSection")?.section_id || null;
  const allApproved = totalSections > 0 && approvedCount === totalSections;

  /** @param {string} sectionId */
  const scrollToSection = useCallback((/** @type {string} */ sectionId) => {
    const el = document.getElementById(`sec-${sectionId}`);
    if (el && typeof el.scrollIntoView === "function") {
      spySuppressUntilRef.current = Date.now() + 1000;
      activeSectionRef.current = sectionId;
      setActiveSectionId(sectionId);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // The mirror direction: scrolling the PREVIEW rail walks the editor along.
  // Same shape as the left spy — section-anchored, debounced, silent — with
  // `lastSyncedRef` shared between both spies as the "we're already on this
  // section" agreement, so neither side ever bounces the other back.
  useEffect(() => {
    if (status !== "ok") return undefined;
    const rail = previewRailRef.current;
    if (!rail) return undefined;
    let pending = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
    const onScroll = () => {
      if (!isDesktopViewport()) return;
      if (Date.now() < previewSpySuppressUntilRef.current) return;
      if (pending) return;
      pending = setTimeout(() => {
        pending = null;
        if (Date.now() < previewSpySuppressUntilRef.current) return;
        const railRect = rail.getBoundingClientRect();
        const line = railRect.top + rail.clientHeight * 0.15;
        let currentId = null;
        for (const s of sectionsRef.current) {
          const el = document.getElementById(`preview-${s.section_id}`);
          if (!el) continue;
          if (el.getBoundingClientRect().top <= line) currentId = s.section_id;
          else break;
        }
        if (currentId && lastSyncedRef.current !== currentId) {
          lastSyncedRef.current = currentId;
          activeSectionRef.current = currentId;
          setActiveSectionId(currentId);
          // scrollToSection suppresses the LEFT spy around its own scroll.
          scrollToSection(currentId);
        }
      }, 150);
    };
    rail.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      rail.removeEventListener("scroll", onScroll);
      if (pending) clearTimeout(pending);
    };
  }, [status, scrollToSection]);
  // Preview → editor: clicking a section heading in the preview opens that
  // card for editing (and flips mobile back to the Edit tab). The preview
  // doubles as the document's table of contents.
  /** @param {string} sectionId */
  const handlePreviewSectionClick = useCallback(
    (/** @type {string | null | undefined} */ sectionId) => {
      if (!sectionId) return;
      setExpandRequest({ id: sectionId, token: Date.now() });
      activeSectionRef.current = sectionId;
      setActiveSectionId(sectionId);
      lastSyncedRef.current = sectionId; // don't bounce the preview back
      if (!isDesktopViewport()) setMobileView("edit");
      requestAnimationFrame(() => scrollToSection(sectionId));
    },
    [scrollToSection]
  );

  // Swipe (mobile): jump to the previous/next section in document order.
  /** @param {number} fromIndex @param {-1 | 1} dir */
  const swipeToSection = useCallback(
    (/** @type {number} */ fromIndex, /** @type {-1 | 1} */ dir) => {
      const target = sections[fromIndex + dir];
      if (target) scrollToSection(target.section_id);
    },
    [sections, scrollToSection]
  );

  // Reset the one-shot resume when navigating to a different report.
  // biome-ignore lint/correctness/useExhaustiveDependencies: uuid is the intended trigger — reset the guard when the report changes.
  useEffect(() => {
    resumedRef.current = false;
  }, [uuid]);
  // Resume where left off: on reopen with partial progress, jump to the next
  // unreviewed section (once). A ?open= deep link from the property space wins:
  // photos → jump to the photo tools; reprepare → open + jump to the advanced
  // (re-prepare from scratch) controls.
  useEffect(() => {
    if (status !== "ok" || resumedRef.current || sections.length === 0) return;
    resumedRef.current = true;
    if (openParam === "photos" && firstPhotoSectionId) {
      const id = firstPhotoSectionId;
      setTimeout(() => scrollToSection(id), 350);
      return;
    }
    if (openParam === "comps" && compsSectionId) {
      setTimeout(() => scrollToSection(compsSectionId), 350);
      return;
    }
    if (openParam === "reprepare") {
      setShowAdvanced(true);
      setTimeout(() => {
        document
          .getElementById("advanced-options")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return;
    }
    if (approvedCount > 0 && !allApproved && nextSection) {
      const id = nextSection.section_id;
      setTimeout(() => scrollToSection(id), 350);
    }
  }, [
    status,
    sections,
    approvedCount,
    allApproved,
    nextSection,
    scrollToSection,
    openParam,
    firstPhotoSectionId,
    compsSectionId,
  ]);

  const mutationBusy =
    !!regenPurchase.pending ||
    regenPurchase.busy ||
    revisingSectionId != null ||
    regenerating ||
    updatingComps ||
    restoreBaseVersion != null ||
    updatingPhotosSectionId != null;
  const busy = mutationBusy || saveStatus === "saving" || !!recoveredDraft;
  const shareDisabled = mutationBusy || continuingToShare || !!recoveredDraft;

  // What the AI pill says while a job runs. Null = no AI job in flight.
  const revisingTitle = revisingSectionId
    ? sectionTitle(sections.find((sec) => sec.section_id === revisingSectionId) || {})
    : "";
  const aiActivity = regenerating
    ? {
        label: regenGuidance.trim()
          ? "Re-preparing your listing with your guidance"
          : "Re-preparing your listing",
        sub: "A fresh draft usually takes 2–3 minutes — it appears here automatically.",
      }
    : revisingSectionId
      ? {
          label: `Rewriting ${revisingTitle || "this section"} with AI`,
          sub: "About a minute — the section refreshes automatically.",
        }
      : updatingComps
        ? {
            label: "Rebuilding comparables around your picks",
            sub: "About a minute — pricing and the analysis refresh automatically.",
          }
        : updatingPhotosSectionId
          ? {
              label: "Rewriting the write-up around your photos",
              sub: "About a minute — it refreshes automatically.",
            }
          : null;
  // Editing is gated on owning the report; non-owners (or signed-out viewers)
  // get a read-only view.
  // Autosave does not lock ordinary typing; version-changing AI actions stay locked.
  const editingDisabled = mutationBusy || !editable || !!recoveredDraft;

  let body;
  if (status === "loading") {
    body = (
      <CenteredMessage>
        <Spinner />
        <p>Loading your listing prep…</p>
      </CenteredMessage>
    );
  } else if (status === "not_found") {
    body = (
      <CenteredMessage>
        <h1>Listing prep not found</h1>
        <p>This link may have expired or been copied incorrectly.</p>
      </CenteredMessage>
    );
  } else if (status === "error" || !doc) {
    body = (
      <CenteredMessage>
        <h1>Something went wrong</h1>
        <p>
          We couldn't load your listing prep. Try refreshing, or email{" "}
          <a href="mailto:help@listino.ai">help@listino.ai</a>.
        </p>
      </CenteredMessage>
    );
  } else {
    // Fresh drafts open on the finished document (mobile); once personalized,
    // editing is the resumed activity.
    const effectiveMobileView = mobileView || ((doc.version || 1) <= 1 ? "preview" : "edit");
    body = (
      <>
        <Eyebrow>Personalize</Eyebrow>
        <Title>{doc.address || "Your listing"}</Title>
        <Subtitle>
          Everything on the right is your finished document — edit on the left and watch it update
          live, then export when it's yours.
        </Subtitle>

        {editable && (doc.version || 1) <= 1 && (
          <ReadyBanner>
            <ReadyTick>✓</ReadyTick>
            <span>
              {creditsReadyBannerText(doc)}
              {videoBetaEligible &&
                " Video walkthroughs are optional and created separately on the Share step."}
            </span>
          </ReadyBanner>
        )}

        {editable && <ExportDeliveryNotice doc={doc} />}
        <SourcesUsed doc={doc} />
        {editable && (regenPurchase.pending || regenPurchase.error || regenPurchaseMessage) && (
          <section
            aria-live="polite"
            aria-label="Re-prepare status"
            style={{
              margin: "16px 0",
              padding: 16,
              border: "1px solid #dce5e4",
              borderRadius: 10,
              overflowWrap: "anywhere",
            }}
          >
            <p>
              {regenPurchase.error ||
                regenPurchaseMessage ||
                "Checking whether your saved re-prepare request started."}
            </p>
            {regenPurchase.pending && (
              <>
                <RegenerateButton
                  type="button"
                  disabled={regenPurchase.busy}
                  onClick={() => {
                    regenPollWindowRef.current.until = Date.now() + 10 * 60 * 1000;
                    void regenPurchase.check();
                  }}
                >
                  {regenPurchase.busy ? "Checking start status…" : "Check re-prepare status"}
                </RegenerateButton>
                {!regenPurchase.receipt && !regenPurchase.busy && (
                  <RegenerateButton
                    type="button"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Retry the same saved re-prepare request? An accepted request will be recovered, not purchased again. If it never started, the current quote is ${formatCredits(reprepareQuote?.cost || 0)}.`
                        )
                      ) {
                        void regenPurchase.retryConfirmed();
                      }
                    }}
                  >
                    Retry same request
                  </RegenerateButton>
                )}
              </>
            )}
          </section>
        )}

        {!editable && (
          <ReadOnlyBanner>
            You're viewing this listing prep.{" "}
            <a href={`/login?next=/reports/${uuid}/edit`}>Sign in</a> as its owner to personalize
            it.
          </ReadOnlyBanner>
        )}

        {editable && recoveredDraft && (
          <ReadOnlyBanner as="section" aria-label="Draft recovery" aria-live="polite">
            <strong>Unsaved edits found in this tab</strong>
            <p>
              {recoveredDraft.baseVersion === documentVersionRef.current
                ? "Restore these edits to continue, or discard them to keep the saved report."
                : "The saved report has changed since this draft. Download your edits to compare them; restoring is disabled to protect the newer report."}
            </p>
            <ActionRow>
              <QuietButton
                type="button"
                onClick={restoreRecoveredDraft}
                disabled={recoveredDraft.baseVersion !== documentVersionRef.current}
              >
                Restore draft
              </QuietButton>
              <QuietButton type="button" onClick={downloadRecoveredDraft}>
                Download draft
              </QuietButton>
              <QuietButton type="button" onClick={discardRecoveredDraft}>
                Discard draft
              </QuietButton>
            </ActionRow>
          </ReadOnlyBanner>
        )}
        {editable && draftBackupFailed && (
          <ReadOnlyBanner role="alert">
            This browser could not keep a recovery copy of your edits. Keep this page open until
            saving succeeds.
          </ReadOnlyBanner>
        )}

        <ViewTabs role="tablist" aria-label="Editor view">
          <ViewTab
            type="button"
            role="tab"
            aria-selected={effectiveMobileView === "edit"}
            $active={effectiveMobileView === "edit"}
            onClick={() => setMobileView("edit")}
          >
            Edit
          </ViewTab>
          <ViewTab
            type="button"
            role="tab"
            aria-selected={effectiveMobileView === "preview"}
            $active={effectiveMobileView === "preview"}
            onClick={openPreviewView}
          >
            Preview
          </ViewTab>
        </ViewTabs>

        <EditorLayout>
          <EditRail $mobileHidden={effectiveMobileView !== "edit"}>
            {sections.map((section, index) => (
              <SectionEditor
                key={section.section_id || `${section.__type__}-${index}`}
                section={section}
                index={index}
                total={sections.length}
                onField={(field, value) => {
                  updateField(index, field, value);
                  unapproveSection(section.section_id);
                  markSectionTouched(section.section_id, section.__type__);
                  activateSection(section.section_id);
                }}
                onList={(field, items) => {
                  updateList(index, field, items);
                  unapproveSection(section.section_id);
                  markSectionTouched(section.section_id, section.__type__);
                  activateSection(section.section_id);
                }}
                onReplace={(next) => {
                  replaceSection(index, next);
                  unapproveSection(section.section_id);
                  markSectionTouched(section.section_id, section.__type__);
                  activateSection(section.section_id);
                }}
                onMove={(dir) => moveSection(index, dir)}
                onRemove={() => removeSection(index)}
                onRevise={(instruction) => handleRevise(index, instruction)}
                isRevising={revisingSectionId === section.section_id}
                onUpdateComps={(addresses, excluded) =>
                  handleUpdateComps(index, addresses, excluded)
                }
                isUpdatingComps={updatingComps}
                compsWarning={compsWarning}
                subjectDetails={doc.content?.property_details}
                onUpdatePhotos={(photos) => handleUpdatePhotos(index, photos)}
                isUpdatingPhotos={updatingPhotosSectionId === section.section_id}
                photosChanged={
                  PHOTO_SECTION_TYPES.has(section.__type__) &&
                  sectionPhotoBasenames(section).join("|") !==
                    (photoBaselineRef.current[section.section_id] ?? "")
                }
                photoManifest={doc.photo_manifest || {}}
                disabled={editingDisabled}
                actionsDisabled={busy}
                readOnly={!editable}
                approved={!!approvals[section.section_id]}
                onUndoRevise={() => undoReviseForSection(section.section_id)}
                // Sections load complete-and-collapsed: the rail is a scannable
                // summary and the agent opens only what they want to change.
                // A deep link (?open=) still expands its target.
                defaultExpanded={
                  section.section_id === deepLinkSectionId || !approvals[section.section_id]
                }
                anchorId={`sec-${section.section_id}`}
                onSwipe={(dir) => swipeToSection(index, dir)}
                diffBefore={revisedBefore[section.section_id]}
                onActivate={() => activateSection(section.section_id)}
                expandRequest={expandRequest}
                onUploadPhoto={editable ? uploadPrepPhoto : undefined}
              />
            ))}

            {editable && (
              <>
                <RestorePanel>
                  <span>
                    Sections are reversible. Restore a removed section or reset this listing prep to
                    the draft you opened.
                  </span>
                  <RestoreActions>
                    {removedSections.map((removed, removedIndex) => (
                      <RestoreButton
                        key={`${removed.section.section_id || removed.section.__type__}-${removedIndex}`}
                        type="button"
                        disabled={busy}
                        onClick={() => restoreRemovedSection(removedIndex)}
                      >
                        Restore {sectionTitle(removed.section)}
                      </RestoreButton>
                    ))}
                    <RestoreButton type="button" disabled={busy} onClick={resetToLoadedDraft}>
                      Reset original draft
                    </RestoreButton>
                    <RestoreButton type="button" disabled={busy} onClick={toggleHistory}>
                      {historyOpen ? "Hide history" : "Version history"}
                    </RestoreButton>
                  </RestoreActions>
                </RestorePanel>
                {historyOpen && (
                  <RestorePanel aria-live="polite">
                    <span>
                      {historyBusy
                        ? "Loading saved versions…"
                        : "Restore a server-saved version. Your current version remains recoverable."}
                    </span>
                    <RestoreActions>
                      {historyVersions.slice(1, 8).map((item) => (
                        <RestoreButton
                          key={item.version}
                          type="button"
                          disabled={historyBusy || dirty}
                          onClick={() => restoreHistoryVersion(item.version)}
                        >
                          Restore v{item.version}
                        </RestoreButton>
                      ))}
                    </RestoreActions>
                    {historyError && (
                      <SaveStatusText $kind="error" role="alert">
                        {historyError}
                      </SaveStatusText>
                    )}
                  </RestorePanel>
                )}
                <AddToListing uuid={uuid} doc={doc} variant="editor" onDocument={mergeDocMeta} />
                <AdvancedToggle
                  id="advanced-options"
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
                >
                  {showAdvanced ? "▾" : "▸"} More options — documents, branding &amp; re-prepare
                  from scratch
                </AdvancedToggle>
                {showAdvanced && (
                  <>
                    <BrandingCard>
                      <BrandingHeader>Listing prep feedback</BrandingHeader>
                      <RegenerateHint>
                        Flag a factual error, weak recommendation, missing section, design/export
                        problem, or overall usefulness concern for the beta team. Feedback is pinned
                        to this version and the section you're viewing.
                      </RegenerateHint>
                      <BrandingGrid>
                        <BrandingField>
                          <AdvancedFieldLabel htmlFor="feedback-kind">
                            Issue type
                          </AdvancedFieldLabel>
                          <AdvancedControl
                            as="select"
                            id="feedback-kind"
                            value={feedbackKind}
                            onChange={(event) => setFeedbackKind(event.target.value)}
                          >
                            <option value="factual_error">Factual error</option>
                            <option value="weak_recommendation">Weak recommendation</option>
                            <option value="missing_section">Missing section</option>
                            <option value="design_export">Design/export problem</option>
                            <option value="usefulness">General usefulness</option>
                          </AdvancedControl>
                        </BrandingField>
                        <BrandingField style={{ flex: 2 }}>
                          <AdvancedFieldLabel htmlFor="feedback-message">
                            What should we check?
                          </AdvancedFieldLabel>
                          <AdvancedControl
                            as="textarea"
                            id="feedback-message"
                            rows={3}
                            maxLength={4000}
                            value={feedbackMessage}
                            onChange={(event) => {
                              setFeedbackMessage(event.target.value);
                              setFeedbackStatus("idle");
                            }}
                          />
                        </BrandingField>
                      </BrandingGrid>
                      <FeedbackActions>
                        <RestoreButton
                          type="button"
                          disabled={feedbackStatus === "sending" || !feedbackMessage.trim()}
                          onClick={submitFeedback}
                        >
                          {feedbackStatus === "sending" ? "Sending…" : "Send feedback"}
                        </RestoreButton>
                        {feedbackStatus === "sent" && (
                          <SaveStatusText as="output">Thanks — feedback sent.</SaveStatusText>
                        )}
                        {feedbackStatus === "error" && (
                          <SaveStatusText $kind="error" role="alert">
                            Add a message and try again.
                          </SaveStatusText>
                        )}
                      </FeedbackActions>
                    </BrandingCard>

                    <RegenerateRow>
                      <RegenerateTitle>Documents for the analysis</RegenerateTitle>
                      <InputDocumentUploader
                        listingUuid={doc?.listing_uuid}
                        documents={doc?.input_documents}
                        disabled={busy}
                        hint={
                          <>
                            Upload an inspection, disclosure, floor plan or HOA packet for its
                            extractable text. It can inform the <strong>next</strong> re-prepare
                            below — not the draft you are looking at now, and not a single section's{" "}
                            <em>Ask AI to revise</em>. Add the document, then re-prepare.
                          </>
                        }
                        onChange={(documents) =>
                          setDoc((current) =>
                            current ? { ...current, input_documents: documents } : current
                          )
                        }
                      />
                    </RegenerateRow>

                    <RegenerateRow>
                      <RegenerateTitle>Re-prepare from scratch</RegenerateTitle>
                      <RegenerateHint>
                        Have AI redraft <strong>every part</strong> — including the comps and
                        pricing. Your hand edits are passed to the AI and woven into the fresh
                        draft; your branding stays. Add optional guidance to steer tone, length, or
                        emphasis.
                      </RegenerateHint>
                      {reprepareQuote && (
                        <RegenerateCost>
                          {reprepareQuote.free
                            ? "Re-prepare · your free re-run"
                            : `Re-prepare · ${formatCredits(reprepareQuote.cost)}`}
                        </RegenerateCost>
                      )}
                      <RegenerateControls>
                        <RegenerateGuidanceInput
                          type="text"
                          aria-label="Regeneration guidance (optional)"
                          placeholder='Optional guidance — e.g. "shorter and warmer" or "lead with the renovated kitchen"'
                          value={regenGuidance}
                          disabled={busy}
                          maxLength={2000}
                          onChange={(e) => setRegenGuidance(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRegenerate();
                          }}
                        />
                        <RegenerateButton
                          ref={reprepareButtonRef}
                          type="button"
                          onClick={handleRegenerate}
                          disabled={busy}
                        >
                          {regenerating ? "Re-preparing…" : "Re-prepare"}
                        </RegenerateButton>
                      </RegenerateControls>
                    </RegenerateRow>

                    <BrandingCard>
                      <BrandingHeader>Your branding</BrandingHeader>
                      <BrandingGrid>
                        <BrandingField>
                          <AdvancedFieldLabel htmlFor="brand-agent">Agent name</AdvancedFieldLabel>
                          <AdvancedControl
                            id="brand-agent"
                            placeholder="Jane Doe"
                            value={branding.agent_name || ""}
                            disabled={editingDisabled}
                            onChange={(e) => updateBranding("agent_name", e.target.value)}
                          />
                        </BrandingField>
                        <BrandingField>
                          <AdvancedFieldLabel htmlFor="brand-brokerage">
                            Brokerage
                          </AdvancedFieldLabel>
                          <AdvancedControl
                            id="brand-brokerage"
                            placeholder="Lone Star Realty"
                            value={branding.brokerage || ""}
                            disabled={editingDisabled}
                            onChange={(e) => updateBranding("brokerage", e.target.value)}
                          />
                        </BrandingField>
                        <BrandingField style={{ flex: "0 0 auto" }}>
                          <AdvancedFieldLabel htmlFor="brand-accent">
                            Accent color
                          </AdvancedFieldLabel>
                          <BrandingColorField>
                            <ColorSwatch
                              id="brand-accent"
                              type="color"
                              value={branding.accent_color || "#0e7e7a"}
                              disabled={editingDisabled}
                              onChange={(e) => updateBranding("accent_color", e.target.value)}
                            />
                            <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                              {branding.accent_color || "#0e7e7a"}
                            </span>
                          </BrandingColorField>
                        </BrandingField>
                      </BrandingGrid>

                      <BrandingAssetLabel as="div">Logo</BrandingAssetLabel>
                      <LogoRow>
                        {branding.logo_url ? (
                          <>
                            <LogoPreview src={branding.logo_url} alt="Your logo" />
                            <IconButton
                              type="button"
                              aria-label="Remove logo"
                              onClick={removeLogo}
                              disabled={editingDisabled}
                            >
                              Remove
                            </IconButton>
                          </>
                        ) : (
                          <LogoUploadLabel aria-disabled={editingDisabled}>
                            Upload logo
                            <HiddenFileInput
                              type="file"
                              accept="image/*"
                              aria-label="Upload logo"
                              disabled={editingDisabled}
                              onChange={(e) => {
                                handleLogoFile(e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </LogoUploadLabel>
                        )}
                      </LogoRow>
                      {branding.logo_url && (
                        <BrandingToggle>
                          <input
                            type="checkbox"
                            checked={branding.show_logo !== false}
                            disabled={editingDisabled}
                            onChange={(e) => updateBranding("show_logo", e.target.checked)}
                          />
                          Show logo on this listing prep
                        </BrandingToggle>
                      )}

                      <BrandingAssetLabel as="div">Broker photo</BrandingAssetLabel>
                      <LogoRow>
                        {branding.headshot_url ? (
                          <>
                            <LogoPreview
                              src={branding.headshot_url}
                              alt="Your headshot"
                              style={{
                                borderRadius: "50%",
                                width: 48,
                                height: 48,
                                objectFit: "cover",
                              }}
                            />
                            <IconButton
                              type="button"
                              aria-label="Remove broker photo"
                              onClick={removeHeadshot}
                              disabled={editingDisabled}
                            >
                              Remove
                            </IconButton>
                          </>
                        ) : (
                          <LogoUploadLabel aria-disabled={editingDisabled}>
                            Upload photo
                            <HiddenFileInput
                              type="file"
                              accept="image/*"
                              aria-label="Upload broker photo"
                              disabled={editingDisabled}
                              onChange={(e) => {
                                handleHeadshotFile(e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </LogoUploadLabel>
                        )}
                      </LogoRow>
                      {branding.headshot_url && (
                        <BrandingToggle>
                          <input
                            type="checkbox"
                            checked={branding.show_headshot !== false}
                            disabled={editingDisabled}
                            onChange={(e) => updateBranding("show_headshot", e.target.checked)}
                          />
                          Show photo on this listing prep
                        </BrandingToggle>
                      )}
                      {logoError && <LogoError>{logoError}</LogoError>}
                    </BrandingCard>
                  </>
                )}
              </>
            )}
          </EditRail>

          <PreviewRail ref={previewRailRef} $mobileHidden={effectiveMobileView !== "preview"}>
            <PreviewBarRow>
              <PreviewBarTitle>Live preview</PreviewBarTitle>
              <ExportButton type="button" disabled={shareDisabled} onClick={continueToShare}>
                {videoBetaEligible ? "Share, downloads & video →" : "Share & downloads →"}
              </ExportButton>
            </PreviewBarRow>
            <ReportPreview
              doc={doc}
              sections={sections}
              branding={branding}
              highlightId={previewHighlightId}
              onSectionClick={handlePreviewSectionClick}
            />
          </PreviewRail>
        </EditorLayout>
      </>
    );
  }

  return (
    <PageContainer>
      <Prompt when={dirty} message={confirmNavigation} />
      <NewNavbar />
      <ContentWrapper>
        <Container style={{ maxWidth: 1240 }}>{body}</Container>
      </ContentWrapper>
      {status === "ok" && doc && (aiActivity || aiDoneMessage) && (
        <AiPill as="output" aria-live="polite" $done={!aiActivity && !!aiDoneMessage}>
          {aiActivity ? <AiPillSpinner aria-hidden="true" /> : <AiPillTick>✓</AiPillTick>}
          <AiPillText>
            {aiActivity ? aiActivity.label : aiDoneMessage}
            {aiActivity && <AiPillSub>{aiActivity.sub}</AiPillSub>}
          </AiPillText>
        </AiPill>
      )}
      {status === "ok" && doc && editable && (
        <SaveBar>
          <Container style={{ maxWidth: 1240 }}>
            <SaveBarInner>
              <ProgressWrap>
                <ProgressText>
                  {allApproved ? (
                    "All set — ready to share"
                  ) : (
                    <>
                      {nextSection ? (
                        <NextLink
                          type="button"
                          onClick={() => scrollToSection(nextSection.section_id)}
                        >
                          Next: {sectionTitle(nextSection)}
                        </NextLink>
                      ) : (
                        "Package ready"
                      )}
                    </>
                  )}
                </ProgressText>
                <SaveStatusText
                  $kind={saveStatus === "error" ? "error" : "default"}
                  style={{ marginTop: 6, display: "block" }}
                >
                  {saveMessage}
                </SaveStatusText>
              </ProgressWrap>
              <SaveActions>
                <BalanceChip compact />
                {saveStatus === "error" && dirty && (
                  <RetrySaveButton type="button" disabled={busy} onClick={autoSave}>
                    Retry save
                  </RetrySaveButton>
                )}
                <ContinueButton type="button" disabled={shareDisabled} onClick={continueToShare}>
                  {videoBetaEligible ? "Continue to Share & Video →" : "Continue to Share →"}
                </ContinueButton>
              </SaveActions>
            </SaveBarInner>
          </Container>
        </SaveBar>
      )}
      {status === "ok" && doc && !editable && (
        <SaveBar>
          <Container style={{ maxWidth: 1240 }}>
            <SaveBarInner>
              <SaveStatusText $kind={saveStatus === "error" ? "error" : "default"}>
                {saveMessage}
              </SaveStatusText>
              <SaveActions>
                {doc.report_pdf_url && (
                  <SecondaryLink
                    href={doc.report_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </SecondaryLink>
                )}
                <SecondaryLink href={`/login?next=/reports/${uuid}/edit`}>
                  Sign in to edit
                </SecondaryLink>
              </SaveActions>
            </SaveBarInner>
          </Container>
        </SaveBar>
      )}
      {reprepareConfirm && reprepareQuote && doc && (
        <ConfirmSpendSheet
          title={
            reprepareQuote.free
              ? "Re-prepare · your free re-run"
              : `Re-prepare · ${formatCredits(reprepareQuote.cost)}`
          }
          cost={reprepareQuote.cost}
          balance={account.credits ? account.credits.balance : null}
          note={
            regenGuidance.trim()
              ? `AI writes a fresh draft with your guidance ("${regenGuidance.trim()}") and weaves your hand edits into it. Takes a few minutes.`
              : "AI writes a fresh draft and weaves your hand edits into it. Takes a few minutes."
          }
          confirmLabel="Re-prepare"
          onConfirm={() => {
            setReprepareConfirm(false);
            void startRegenerate();
          }}
          onClose={() => setReprepareConfirm(false)}
        />
      )}
      {creditsBlock && doc && (
        <CreditsPaywallSheet
          block={creditsBlock}
          onClose={() => setCreditsBlock(null)}
          returnFocusRef={reprepareButtonRef}
          listingUuid={typeof doc.listing_uuid === "string" ? doc.listing_uuid : undefined}
        />
      )}
      <NewFooter />
    </PageContainer>
  );
};

export default ReportEditorPage;
